
import React, { PropsWithChildren, useEffect, useRef, useState } from 'react';
import { MappedComponentProperties } from '@adobe/aem-react-editable-components';
import { transformHTMLParserLinks } from '../../utils/rewriteLink/rewriteLink';
import './PersonalInformationForm.css';
import {
    InputBase,
    InputCheckbox,
} from '../../component-library/index';
import { regexValidation, checkExistsDiacriticSpecialCharacters, wrapTrademarkSuperscript } from '../../utils/cob-utils';
import SessionStorage from '../../utils/SessionStorage';
import { useAcquisitionContext } from '../../providers/AcquisitionProvider/AcquisitionProvider';
import { areStringsEqual, formatDateOfBirth, getAppData, handlePartnerRedirect, validateFirstAndLastName } from '../../utils/acquisition-utils';
import { IPersonalInfoErrors } from '../../providers/AcquisitionProvider/models';
import { Address, PQofOneApplicationResponse } from '../../types/models/PQofOneApplicantResponse';
import { blackboxUtils } from '../../utils/blackbox-utils';
import useBriteVerify from '../../hooks/useBriteVerify';
import NameField from '../FieldContainers/NameField/NameField';
import EmailField, { TEmailValidationError } from '../FieldContainers/EmailField/EmailField';
import { MiddleInitialField } from '../FieldContainers/MiddleInitialField/MiddleInitialField';
import DateOfBirthField from '../FieldContainers/DateOfBirthField/DateOfBirthField';
import { PathUtils } from '@adobe/aem-spa-page-model-manager';
import SsnField from '../FieldContainers/SsnField/SsnField';
import SuffixField from '../FieldContainers/SuffixField/SuffixField';
import MonthlyIncomeField from '../FieldContainers/MonthlyIncomeField/MonthlyIncomeField';
import PhoneNumberField from '../FieldContainers/PhoneNumberField/PhoneNumberField';
import { Button, Row, Col, Section } from '../../importCL';
import GroupedAddressFields from '../FieldContainers/GroupedAddressFields/GroupedAddressFields';
import SlideDown from '../SlideDown/SlideDown';
import { Offer } from '../../types/responses/PhaSecuredApplicationResponse';
import { useFeatureFlags } from '../../providers/FeatureFlagProvider/FeatureFlagProvider';
import useAcquisitionProviderFacade from '../../facades/AcquisitionProviderFacade/useAcquisitionProviderFacade';
import NameFieldWithProvider from '../FieldContainers/NameField/NameFieldWithProvider';
import MiddleInitialFieldWithProvider from '../FieldContainers/MiddleInitialField/MiddleInitialFieldWithProvider';
import SuffixFieldWithProvider from '../FieldContainers/SuffixField/SuffixFieldWithProvider';
import { TAcquisitionState, TBooleanField, TInputField } from '../../providers/ModernAcquisitionProvider/types';
import { useAllFieldSubscription } from '../../providers/ModernAcquisitionProvider/useSubscription';
import { generateFieldErrorMessage } from '../../utils/error-message';
import EmailFieldWithProvider from '../FieldContainers/EmailField/EmailFieldWithProvider';
import GroupedAddressFieldsHomePrimary from '../FieldContainers/GroupedAddressFields/variants/GroupedAddressFieldsHomePrimary';
import PhoneNumberFieldWithProvider from '../FieldContainers/PhoneNumberField/PhoneNumberFieldWithProvider';
import { DateOfBirthFieldWithProvider } from '../FieldContainers/DateOfBirthField/DateOfBirthFieldWithProvider';
import AcquisitionErrorToggleEvent from '../ApplicationCta/AcquisitionErrorToggleEvent';

interface ISingleString {
    value: string;
}

interface IPersonalInfoObject {
    value: string;
    valid: boolean;
}

interface IPersonalInformationFormProps extends MappedComponentProperties {
    author?: boolean;
    pqOfOneRedirect?: string;
    pqOfOnePushdownCode?: string;
    version?: 'preApproved' | 'skipPQ' | 'skipPQ-Prefilled' | 'pqOfOne' | 'partnerHostedVersion' | 'versionD' | 'versionB' | 'versionF';
    topBorderEnabled?: boolean;
    bottomBorderEnabled?: boolean;
    topPaddingEnabled?: boolean;
    headline?: string;
    title?: string;
    subtitle?: string;
    wiDisclaimer?: string;
    mailingAddressText?: string;
    showOnAppEnabled?: boolean;
    paFormInformationSubhead?: string;
    emailMessage?: string;
    phoneMessage?: string;
    ssnMessage?: string;
    incomeMessage?: string;
    incomeDescription?: string;
    errorTitle?: string;
    errorDesc?: string;
    requirePhoneNumberEnabled?: boolean;
    requireMonthlyIncomeEnabled?: boolean;
    requireEmailEnabled?: boolean;
    emailAutofill?: ISingleString[];
    incomePopupEnabled?: boolean;
    incomePopupHeadline?: string;
    incomePopupSubHeadline?: string;
    incomePopupCtaKeep?: string;
    incomePopupCtaDismiss?: string;
    aptUnitPoBox?: boolean;
    applicationOfferResponse?: string;
    showingGrossIncomeVariant?: boolean;
    hidingPANameAndAddressOnLoad?: boolean;
    suggestedAddressEnabled?: boolean;
    suggestedAddressHeadline?: string;
    suggestedAddressSubHeadline?: string;
    suggestedAddressAcceptCtaText?: string;
    suggestedAddressDeclineCtaText?: string;
    avcThreshold?: string;
    hidePIOnPQOfOneEnabled?: boolean;
    homeAddressPrimary?: boolean;
    advancedMasking?: boolean;
    homeAddressInputText?: string;
    mailingAddressCheckboxText?: string;
}

const oldToNewMapping: Record<string, string> = {
    'versionD': 'skipPQ',
    'versionB': 'preApproved',
    'versionF': 'pqOfOne',
    'partnerHostedVersion': 'partnerHostedVersion',
};
export const PersonalInformationFormEditConfig = {
    emptyLabel: 'Personal Information Form',
    isEmpty: (props: PropsWithChildren<IPersonalInformationFormProps>) => {
        return !props || !props.title;
    },
};

const PersonalInformationForm = (props: PropsWithChildren<IPersonalInformationFormProps>): JSX.Element => {

    const { personalInfoForm, setPersonalInfoForm, personalInfoErrors,
        setPersonalInfoErrors, isApplicationInfoRecieved, setIsPqOfOneGetComplete,
        setHomeAddressValidateOnSubmit, setIsSamePersonError, authorizedUserInfo, setIsPartnerHostedGetComplete,
        setIsEmailBlurred, homeAddressPrimaryRef } = useAcquisitionContext();
    const regexContainer = regexValidation();
    const [hideRow, setHideRow] = useState(true);
    const [hidePhoneMessage, setHidePhoneMessage] = useState(true);
    const [isPhonePrefilled, setIsPhonePrefilled] = useState(false);
    const [elist] = useState<String[]>(PathUtils.getMetaPropertyValue('cq:elist')?.split(',').map(email => atob(email)) ?? []);
    const [plist] = useState<String[]>(PathUtils.getMetaPropertyValue('cq:plist')?.split(',').map(phone => atob(phone)) ?? []);
    const emailAutofill = PathUtils.getMetaPropertyValue('cq:emailAutofill')?.split(',').map(email => ({ value: email })) ?? null;
    const { showOnAppEnabled = false, version = 'preApproved', subtitle = '', paFormInformationSubhead = '' } = props;
    const updatedPersonalInfoVersion = version.includes('version') ? oldToNewMapping[version] : version;
    const [firstEdit, setFirstEdit] = useState(true);
    const [secondEdit, setSecondEdit] = useState(true);
    const phoneInputRef = useRef<HTMLInputElement>();
    const monthlyIncomeRef = useRef<HTMLInputElement>();

    const [lastPhone, setLastPhone] = useState<{ value: string, result: boolean, validationResult?: any }>({ value: '', result: false });

    const [mailingCheckboxSelected, setMailingCheckboxSelected] = useState(false);
    homeAddressPrimaryRef.current.value = props.homeAddressPrimary;

    const { emailValidationApi, phoneValidationApi } = useBriteVerify();

    const hidePIOnPQOfOne = updatedPersonalInfoVersion === 'pqOfOne' && props.hidePIOnPQOfOneEnabled;

    const [showIcon, setShowIcon] = useState(false);
    const [hideAddressOnPA, setHideAddressOnPA] = useState(props.hidingPANameAndAddressOnLoad);
    const { getFeatureFlags } = useFeatureFlags();
    const {
        getPersonalInformation,
        personalInformationWrite,
        setIsHomeAddressShown,
        getIsHomeAddressShown,
        getPersonalInformationDob,
        getAuthorizedUserDob,
    } = useAcquisitionProviderFacade();
    const { fields: acquisitionDataUpdate } = useAllFieldSubscription();

    async function validateOffer(offerId?: string, pqOfOneProgramCode?: string) {
        return await fetch(`/services/pqOfOneApplicant?offerId=${offerId}`)
            .then((result) => result.json())
            .then(async (data) => {
                if (!data.programCode) {
                    throw new Error('Offer expired');
                }
                SessionStorage.set('selectedProgramCode', (pqOfOneProgramCode || data.programCode) as string);
                const personalInfo = formatPersonalInfo(data.applicant);
                await SessionStorage.setCipherWithCurrentScope('personalInfo', personalInfo);
                return personalInfo;
            });
    }

    function formatPersonalInfo(information: PQofOneApplicationResponse) {
        const birthMonth = information.doB.slice(4, 6);
        const birthDay = information.doB.slice(6);
        const birthYear = information.doB.slice(0, 4);

        let mailingAddress: Address = {};
        let homeAddress: Address = {};
        let ssn = '';
        let sameHomeAddress = false;

        information.personalIdentityInformation.forEach((identifier) => {
            if (identifier.piiType === 'SSN') {
                ssn = identifier.value;
            }
        });

        if (information.addresses.length === 1) {
            sameHomeAddress = true;
            mailingAddress = information.addresses[0];
        } else {
            information.addresses.forEach((address) => {
                if (address.isPOBox || address.type === 'Mailing') {
                    mailingAddress = address;
                    setHideRow(false);
                } else if (address.type === 'Primary') {
                    homeAddress = address;
                }
            });
        }

        const deviceInfo = blackboxUtils.getBlackbox();

        return {
            FirstName: information.firstName,
            MiddleInitial: information.middleInitial || '',
            LastName: information.lastName,
            NameSuffix: '',
            EmailAddress: information.email,
            MailingAddressLine1: mailingAddress.addressLine1,
            MailingAddressLine2: (mailingAddress.addressLine2 || '').replace(/#/g, ''),
            MailingCity: mailingAddress.city,
            MailingState: mailingAddress.state,
            MailingAddressStateSelectedText: '',
            MailingZipCode: mailingAddress.zipCode,
            IsMailingHomeAddressSame: sameHomeAddress,
            HomeAddressLine1: homeAddress.addressLine1,
            HomeAddressLine2: (homeAddress.addressLine2 || '').replace(/#/g, ''),
            HomeCity: homeAddress.city,
            HomeState: homeAddress.state,
            HomeAddressStateSelectedText: homeAddress.state,
            HomeZipCode: homeAddress.zipCode,
            PrimaryPhoneNumber: null,
            SecondaryPhoneNumber: null,
            MonthlyIncome: null,
            Ssn: ssn,
            AdditionalQuestions: [],
            DOBDay: birthDay,
            DOBMonth: birthMonth,
            DOBYear: birthYear,
            DateOfBirth: birthMonth + '/' + birthDay + '/' + birthYear,
            IsDataEntryPage: true,
            ioBlackBox: deviceInfo && deviceInfo.Blackbox ? deviceInfo.Blackbox.ThirdParty : '',
            fpBB: deviceInfo && deviceInfo.Blackbox ? deviceInfo.Blackbox.FirstParty : '',
        };
    }

    function getPhaPersonalInformation(information: any) {
        const partyInformation = information.parties[0];
        const addressInformation: any[] = partyInformation.addresses;

        const dateParts = partyInformation.dateOfBirth.split(/[\/-]/);
        const birthDay = dateParts[2];
        const birthMonth = dateParts[1];
        const birthYear = dateParts[0];

        const deviceInfo = blackboxUtils.getBlackbox();
        const rawMonthlyIncome = partyInformation.income.amount;

        if (
            addressInformation.length > 1 ||
            addressInformation[0].poBox ||
            addressInformation[0].usageType !== 'HOME'
        ) {
            setHideRow(false);
        }

        const personalInformationObj = {
            FirstName: partyInformation.firstName,
            MiddleInitial: partyInformation.middleInitial,
            LastName: partyInformation.lastName,
            NameSuffix: partyInformation.suffix,
            EmailAddress: partyInformation.emails[0].value,
            Ssn: '',
            AdditionalQuestions: [],
            DOBDay: birthDay,
            DOBMonth: birthMonth,
            DOBYear: birthYear,
            DateOfBirth: `${birthMonth}/${birthDay}/${birthYear}`,
            IsDataEntryPage: true,
            ioBlackBox: deviceInfo && deviceInfo.Blackbox ? deviceInfo.Blackbox.ThirdParty : '',
            fpBB: deviceInfo && deviceInfo.Blackbox ? deviceInfo.Blackbox.FirstParty : '',
            PrimaryPhoneNumber: formatPhoneForSessionStorage(partyInformation.phones[0].value),
            MonthlyIncome: new Intl.NumberFormat('en-US', {
                style: 'currency',
                currency: 'USD',
                minimumFractionDigits: 0,
                maximumFractionDigits: 0,
            }).format(rawMonthlyIncome),
        };

        if (addressInformation.length > 1) {
            const mailingAddressInformation = addressInformation.find((addr) => addr.usageType === 'MAILING');
            const homeAddressInformation = addressInformation.find((addr) => addr.usageType === 'HOME');

            Object.assign(personalInformationObj, {
                MailingAddressLine1: mailingAddressInformation.addressLine1,
                MailingAddressLine2: (mailingAddressInformation.addressLine2)?.replace(/#/g, ''),
                MailingCity: mailingAddressInformation.city,
                MailingState: mailingAddressInformation.state,
                MailingAddressStateSelectedText: '',
                MailingZipCode: mailingAddressInformation.zipCode.slice(0, 5),
                IsMailingHomeAddressSame: false,
                HomeAddressLine1: homeAddressInformation.addressLine1,
                HomeAddressLine2: (homeAddressInformation.addressLine2)?.replace(/#/g, ''),
                HomeCity: homeAddressInformation.city,
                HomeState: homeAddressInformation.state,
                HomeAddressStateSelectedText: '',
                HomeZipCode: homeAddressInformation.zipCode.slice(0, 5),
            });
        } else {
            Object.assign(personalInformationObj, {
                MailingAddressLine1: addressInformation[0].addressLine1,
                MailingAddressLine2: (addressInformation[0].addressLine2)?.replace(/#/g, ''),
                MailingCity: addressInformation[0].city,
                MailingState: addressInformation[0].state,
                MailingAddressStateSelectedText: '',
                MailingZipCode: addressInformation[0].zipCode.slice(0, 5),
                IsMailingHomeAddressSame: true,
                HomeAddressLine1: '',
                HomeAddressLine2: '',
                HomeCity: '',
                HomeState: '',
                HomeAddressStateSelectedText: '',
                HomeZipCode: '',
            });
            setHideRow(true);
        }

        return personalInformationObj;
    }

    const isSamePersonAuthorizedUserSetter = () => {
        const acquisitionData = getPersonalInformation();
        const piDob = getPersonalInformationDob();

        if (acquisitionData?.firstName?.value && acquisitionData?.lastName?.value && piDob) {
            const authorizedUserDOB = getAuthorizedUserDob();
            const authorizedUserString = [acquisitionData?.authorizedUserFirstName.value, acquisitionData?.authorizedUserLastName.value, formatDateOfBirth(authorizedUserDOB)].join('');
            const personalInfoFormString = [acquisitionData?.firstName?.value, acquisitionData?.lastName?.value, formatDateOfBirth(piDob)].join('');
            setIsSamePersonError(areStringsEqual(authorizedUserString, personalInfoFormString));
        }
    };

    const checkEmpty = (value: string) => {
        return value === undefined || value === null || value.length <= 0;
    };

    const setErrorList = (elementName: string, setErrorClass: boolean, element: IPersonalInfoObject, fieldNameError: string, typeMessageError: string = 'RequiredField') => {
        if (!elementName || !element || !fieldNameError) throw new Error('Invalid argument(s).');

        if (setErrorClass) {
            setPersonalInfoForm((personalInfoForm: any) => ({ ...personalInfoForm, [elementName]: { value: element?.value, valid: false } }));

            setPersonalInfoErrors((prev: IPersonalInfoErrors) => {
                return {
                    ...prev,
                    [fieldNameError ?? elementName]: typeMessageError,
                };
            });

        } else {
            setPersonalInfoForm((personalInfoForm: any) => ({ ...personalInfoForm, [elementName]: { value: element?.value, valid: true } }));
            setPersonalInfoErrors((prev: IPersonalInfoErrors) => {
                return {
                    ...prev,
                    [fieldNameError ?? elementName]: 'None',
                };
            });
        }
    };

    const validateField = (fieldName: string, field: IPersonalInfoObject, allowRegex: RegExp | null, disallowRegex: any, fieldNameError: string) => {
        if (!fieldName || !field || !fieldNameError) throw new Error('Invalid argument(s).');

        const isInvalid = fieldName.includes('Name') ? !validateFirstAndLastName(field?.value) : !allowRegex!.test(field?.value);
        const isMissingAttributes = checkEmpty(field?.value) || isInvalid;
        const hasDisallowedAttributes: boolean = disallowRegex!.test(field?.value) || checkExistsDiacriticSpecialCharacters(field?.value);

        if (isMissingAttributes) {
            setErrorList(fieldName, isMissingAttributes, field, fieldNameError, 'RequiredField');
            return false;
        }
        setErrorList(fieldName, hasDisallowedAttributes, field, fieldNameError, 'NotValid');
        return hasDisallowedAttributes ? false : true;
    };

    const validateEmailWithBriteVerify = async (value: string): Promise<TEmailValidationError> => {
        if (!props.requireEmailEnabled) {
            return undefined;
        }

        const isAdobeMode = SessionStorage.contains('adobeMode');
        const useEmailApi = SessionStorage.contains('useEmailApi');
        const emailValue = value.toUpperCase();

        if (elist.includes(emailValue.toLowerCase().substring(emailValue.indexOf('@') + 1))) {
            return 'ThirdParty';
        } else if ((!props.author && !isAdobeMode) || (isAdobeMode && useEmailApi)) {
            return await emailValidationApi(value) ? undefined : 'ThirdParty';
        }
        return undefined;
    };

    const completeEmailValidation = (value: string, error: TEmailValidationError): void => {
        setShowIcon(!error);
        if (!props.requireEmailEnabled) return;
        setIsEmailBlurred(true);
        setErrorList('email', !!error, { value, valid: !error }, 'EmailAddress', error);
    };

    const validatePhoneNumberWithBriteVerify = async (phoneNumber: IPersonalInfoObject) => {
        if (!props.requirePhoneNumberEnabled) {
            return true;
        }

        const isAdobeMode = SessionStorage.contains('adobeMode');
        const usePhoneApi = SessionStorage.contains('usePhoneApi');
        const phone = phoneNumber.value && phoneNumber.value.replace(/[^0-9]/g, '');
        const missingValue = checkEmpty(phone);
        if (plist.length > 0 && plist.includes(phone)) {
            setErrorList('phoneNumber', true, phoneNumber, 'PrimaryPhoneNumber');
            return false;
        }
        if (missingValue) {
            setErrorList('phoneNumber', missingValue, phoneNumber, 'PrimaryPhoneNumber');
            return false;
        } else if ((!props.author && !isAdobeMode) || (isAdobeMode && usePhoneApi)) {
            let isValid = true;
            let isThirdPartyError = false;
            if (phone === lastPhone.value) {
                isValid = lastPhone.result;
            } else {
                setErrorList('phoneNumber', false, phoneNumber, 'PrimaryPhoneNumber');
                const validationResult = await phoneValidationApi(phone);
                isValid = !!validationResult;
                if (validationResult) {
                    SessionStorage.set('bvrslt', btoa(JSON.stringify(validationResult)));
                } else if (phone?.length === 10) {
                    isThirdPartyError = true;
                }
                setLastPhone({ value: phone, result: isValid, validationResult });
            }

            if (isValid) {
                setErrorList('phoneNumber', false, phoneNumber, 'PrimaryPhoneNumber');
            } else if (isPhonePrefilled) {
                personalInformationWrite({
                    phoneNumber: { value: '', state: 'neutral', errorType: 'None' }
                });

                setIsPhonePrefilled(false);
            } else {
                setErrorList('phoneNumber', true, phoneNumber, 'PrimaryPhoneNumber', isThirdPartyError ? 'ThirdParty' : 'RequiredField');
            }

            return isValid;
        } else if (!regexContainer.allow.phone.test(phone)) {
            setErrorList('phoneNumber', true, phoneNumber, 'PrimaryPhoneNumber', 'NotValid');
            return false;
        }

        setErrorList('phoneNumber', false, phoneNumber, 'PrimaryPhoneNumber');
        return true;
    };

    const startValidation = async () => {
        const updatedErrorState: IPersonalInfoErrors = {} as IPersonalInfoErrors;
        Object.assign(updatedErrorState, personalInfoErrors);
        const postDataEl = document.getElementById('pq-of-one-post-data');

        if (!postDataEl && !props.author) {
            handlePartnerRedirect(props.pqOfOneRedirect, props.pqOfOnePushdownCode);
        }

        const { sourceId, descriptorId } = postDataEl.dataset;
        const { origin, pathname } = window.location;
        const params = new URLSearchParams(window.location.search);

        if (sourceId) {
            params.set('C1BSourceID', sourceId);
        }
        if (descriptorId) {
            params.set('C1BDescriptorID', descriptorId);
        }
        window.history.pushState({}, '', `${origin}${pathname}?${params.toString()}`);

        let { offerId } = postDataEl.dataset;
        if (offerId) {
            SessionStorage.set('offerId', offerId);
        } else {
            offerId = SessionStorage.tryGet('offerId', '');
            if (!offerId) {
                handlePartnerRedirect(props.pqOfOneRedirect, props.pqOfOnePushdownCode);
                return;
            }
        }

        const { authorizedUserCheckbox, programCode,
            firstName, lastName, middleInitial,
            dobMonth, dobDay, dobYear,
            email, ssn,
            mailingAddr1, mailingAddr2, mailingCity, mailingState, mailingZip,
            homeAddr1, homeAddr2, homeCity, homeState, homeZip } = postDataEl.dataset;

        if (authorizedUserCheckbox === 'on') {
            SessionStorage.set('pqOfOneAuthorizedUserShowing', 'true');
        }

        await validateOffer(offerId, programCode ?? '')
            .then((personalInfoPromise) => {
                personalInformationWrite({
                    dateOfBirthDay: { value: dobDay || personalInfoPromise.DOBDay, state: 'neutral', errorType: 'None' },
                    dateOfBirthMonth: { value: dobMonth || personalInfoPromise.DOBMonth, state: 'neutral', errorType: 'None' },
                    dateOfBirthYear: { value: dobYear || personalInfoPromise.DOBYear, state: 'neutral', errorType: 'None' },
                    emailAddress: { value: email || personalInfoPromise.EmailAddress, state: 'neutral', errorType: 'None' },
                    firstName: { value: firstName || personalInfoPromise.FirstName, state: 'neutral', errorType: 'None' },
                    homeAddressCity: { value: homeCity || personalInfoPromise.HomeCity, state: 'neutral', errorType: 'None' },
                    homeAddressLineOne: { value: homeAddr1 || personalInfoPromise.HomeAddressLine1, state: 'neutral', errorType: 'None' },
                    homeAddressLineTwo: { value: homeAddr2 || personalInfoPromise.HomeAddressLine2, state: 'neutral', errorType: 'None' },
                    homeAddressState: { value: homeState || personalInfoPromise.HomeState, state: 'neutral', errorType: 'None' },
                    homeAddressZipCode: { value: homeZip || personalInfoPromise.HomeZipCode, state: 'neutral', errorType: 'None' },
                    lastName: { value: lastName || personalInfoPromise.LastName, state: 'neutral', errorType: 'None' },
                    mailingAddressCity: { value: mailingCity || personalInfoPromise.MailingCity, state: 'neutral', errorType: 'None' },
                    mailingAddressLineOne: { value: mailingAddr1 || personalInfoPromise.MailingAddressLine1, state: 'neutral', errorType: 'None' },
                    mailingAddressLineTwo: { value: mailingAddr2 || personalInfoPromise.MailingAddressLine2, state: 'neutral', errorType: 'None' },
                    mailingAddressState: { value: mailingState || personalInfoPromise.MailingState, state: 'neutral', errorType: 'None' },
                    mailingAddressZipCode: { value: mailingZip || personalInfoPromise.MailingZipCode, state: 'neutral', errorType: 'None' },
                    middleInitial: { value: middleInitial || personalInfoPromise.MiddleInitial, state: 'neutral', errorType: 'None' },
                    monthlyIncome: { value: personalInfoPromise.MonthlyIncome || getPersonalInformation().monthlyIncome.value, state: 'neutral', errorType: 'None' },
                    phoneNumber: { value: personalInfoPromise.PrimaryPhoneNumber || getPersonalInformation().phoneNumber.value, state: 'neutral', errorType: 'None' },
                    ssn: { value: ssn || personalInfoPromise.Ssn, state: 'neutral', errorType: 'None' },
                    suffix: { value: personalInfoPromise.NameSuffix, state: 'neutral', errorType: 'None' },
                });
                setIsHomeAddressShown(false);
                setIsPqOfOneGetComplete(true);
            }).catch(() => {
                handlePartnerRedirect(props.pqOfOneRedirect, props.pqOfOnePushdownCode);
            });
    };

    const startValidationForPartnerHosted = async () => {
        const applicationOfferResponse = JSON.parse(props.applicationOfferResponse) as Offer;

        let sourceIdString = '';
        let descriptorIdString = '';

        const trafficAttributes = applicationOfferResponse.trafficAttribution;
        trafficAttributes.forEach((item: { name: string; value: string }) => {
            const { name, value } = item;
            if (name === 'SOURCE_ID') {
                sourceIdString = value;
            }
            if (name === 'DESCRIPTOR_ID') {
                descriptorIdString = value;
            }
        })

        const currentUrl = new URL(window.location.href);
        if (!currentUrl.searchParams.has('C1BSourceID')) {
            currentUrl.searchParams.set('C1BSourceID', sourceIdString);
            currentUrl.searchParams.set('C1BDescriptorID', descriptorIdString);

            const urlWithIds = currentUrl.toString();
            window.history.pushState({ path: urlWithIds }, '', urlWithIds);
        }

        SessionStorage.set('offerId', applicationOfferResponse.offers[0].offerId);
        SessionStorage.set('selectedProgramCode', applicationOfferResponse.offers[0].card.programCode);

        let phaPersonalInfo;
        try {
            phaPersonalInfo = getPhaPersonalInformation(applicationOfferResponse);
        } catch (e) {
            handlePartnerRedirect(props.pqOfOneRedirect);
        }

        await SessionStorage.setCipherWithCurrentScope('personalInfo', phaPersonalInfo);
        // in other flows this will happen in initSkipPqApp()
        SessionStorage.setObjectWithCurrentScope('applicationOfferResponse', applicationOfferResponse);

        const mappedCardInfo = applicationOfferResponse.offers[0].card.keyCardFeatures.cardVariations.map((card, i) => {
            return {
                Selected: card.default,
                CardName: card.name,
                CardDescription: card.description,
                PremiumFee: card.premiumFee,
                IsFeeRecurring: card.feeRecurring,
                IsPremium: card.premiumFee && card.premiumFee > 0,
                ProgramPlasticCode: i + 1,
                Code: card.cardMaterialId,
            };
        });
        SessionStorage.setObjectWithCurrentScope('cardInfo', mappedCardInfo);

        const dateParts = (phaPersonalInfo.DateOfBirth as string).split(/[\/-]/);
        personalInformationWrite({
            dateOfBirthDay: { value: dateParts[1], state: 'neutral', errorType: 'None' },
            dateOfBirthMonth: { value: dateParts[0], state: 'neutral', errorType: 'None' },
            dateOfBirthYear: { value: dateParts[2], state: 'neutral', errorType: 'None' },
            emailAddress: { value: phaPersonalInfo.EmailAddress, state: 'neutral', errorType: 'None' },
            firstName: { value: phaPersonalInfo.FirstName, state: 'neutral', errorType: 'None' },
            homeAddressCity: { value: phaPersonalInfo.HomeCity, state: 'neutral', errorType: 'None' },
            homeAddressLineOne: { value: phaPersonalInfo.HomeAddressLine1, state: 'neutral', errorType: 'None' },
            homeAddressLineTwo: { value: phaPersonalInfo.HomeAddressLine2, state: 'neutral', errorType: 'None' },
            homeAddressState: { value: phaPersonalInfo.HomeState, state: 'neutral', errorType: 'None' },
            homeAddressZipCode: { value: phaPersonalInfo.HomeZipCode, state: 'neutral', errorType: 'None' },
            lastName: { value: phaPersonalInfo.LastName, state: 'neutral', errorType: 'None' },
            mailingAddressCity: { value: phaPersonalInfo.MailingCity, state: 'neutral', errorType: 'None' },
            mailingAddressLineOne: { value: phaPersonalInfo.MailingAddressLine1, state: 'neutral', errorType: 'None' },
            mailingAddressLineTwo: { value: phaPersonalInfo.MailingAddressLine2, state: 'neutral', errorType: 'None' },
            mailingAddressState: { value: phaPersonalInfo.MailingState, state: 'neutral', errorType: 'None' },
            mailingAddressZipCode: { value: phaPersonalInfo.MailingZipCode, state: 'neutral', errorType: 'None' },
            middleInitial: { value: phaPersonalInfo.MiddleInitial, state: 'neutral', errorType: 'None' },
            monthlyIncome: { value: phaPersonalInfo.MonthlyIncome, state: 'neutral', errorType: 'None' },
            phoneNumber: { value: phaPersonalInfo.PrimaryPhoneNumber, state: 'neutral', errorType: 'None' },
            suffix: { value: phaPersonalInfo?.NameSuffix ?? '', state: 'neutral', errorType: 'None' },
        });
        setIsHomeAddressShown(!!phaPersonalInfo.HomeAddressLine1);
        setIsPartnerHostedGetComplete(true);
    };

    const formatPhoneForSessionStorage = (phone: string) => {
        const onlyNumbers = phone.replace(/[^0-9]/g, '').slice(0, 10);
        const formatted = onlyNumbers
            ? (onlyNumbers.length < 4
                ? `(${onlyNumbers}`
                : onlyNumbers.length > 3 && onlyNumbers.length <= 6
                    ? `(${onlyNumbers.slice(0, 3)}) ${onlyNumbers.slice(3)}`
                    : `(${onlyNumbers.slice(0, 3)}) ${onlyNumbers.slice(
                        3,
                        6,
                    )}-${onlyNumbers.slice(6)}`
            ) : '';

        return formatted;
    };

    const restoreFromSavedAppData = async (): Promise<boolean> => {
        const existingAppData = await getAppData();
        if (!existingAppData || Object.keys(existingAppData).length === 0) return false;
        if (existingAppData?.PersonalInformation && (updatedPersonalInfoVersion === 'skipPQ' || updatedPersonalInfoVersion === 'skipPQ-Prefilled')) {
            const pi = existingAppData.PersonalInformation;
            const hasHomeAddress = !!pi.HomeAddressLine1 && !pi.IsMailingHomeAddressSame;
            if (hasHomeAddress) {
                setHideRow(false);
                setMailingCheckboxSelected(true);
            }

            personalInformationWrite({
                dateOfBirthDay: { value: pi.DOBDay, state: 'neutral', errorType: 'None' },
                dateOfBirthMonth: { value: pi.DOBMonth, state: 'neutral', errorType: 'None' },
                dateOfBirthYear: { value: pi.DOBYear, state: 'neutral', errorType: 'None' },
                emailAddress: { value: pi.EmailAddress || '', state: 'neutral', errorType: 'None' },
                firstName: { value: pi.FirstName || '', state: 'neutral', errorType: 'None' },
                homeAddressCity: { value: pi.HomeCity || '', state: 'neutral', errorType: 'None' },
                homeAddressLineOne: { value: pi.HomeAddressLine1 || '', state: 'neutral', errorType: 'None' },
                homeAddressLineTwo: { value: pi.HomeAddressLine2 || '', state: 'neutral', errorType: 'None' },
                homeAddressState: { value: pi.HomeState || '', state: 'neutral', errorType: 'None' },
                homeAddressZipCode: { value: pi.HomeZipCode || '', state: 'neutral', errorType: 'None' },
                lastName: { value: pi.LastName || '', state: 'neutral', errorType: 'None' },
                mailingAddressCity: { value: pi.MailingCity || '', state: 'neutral', errorType: 'None' },
                mailingAddressLineOne: { value: pi.MailingAddressLine1 || '', state: 'neutral', errorType: 'None' },
                mailingAddressLineTwo: { value: pi.MailingAddressLine2 || '', state: 'neutral', errorType: 'None' },
                mailingAddressState: { value: pi.MailingState || '', state: 'neutral', errorType: 'None' },
                mailingAddressZipCode: { value: pi.MailingZipCode || '', state: 'neutral', errorType: 'None' },
                middleInitial: { value: pi.MiddleInitial || '', state: 'neutral', errorType: 'None' },
                monthlyIncome: { value: pi.MonthlyIncome || '', state: 'neutral', errorType: 'None' },
                phoneNumber: { value: pi.PrimaryPhoneNumber || '', state: 'neutral', errorType: 'None' },
                ssn: { value: pi.Ssn || '', state: 'neutral', errorType: 'None' },
                suffix: { value: '', state: 'neutral', errorType: 'None' },
            });

            if (props.homeAddressPrimary && hasHomeAddress) {
                personalInformationWrite({
                    homeAddressCity: { value: pi.MailingCity || '', state: 'neutral', errorType: 'None' },
                    homeAddressLineOne: { value: pi.MailingAddressLine1 || '', state: 'neutral', errorType: 'None' },
                    homeAddressLineTwo: { value: pi.MailingAddressLine2 || '', state: 'neutral', errorType: 'None' },
                    homeAddressState: { value: pi.MailingState || '', state: 'neutral', errorType: 'None' },
                    homeAddressZipCode: { value: pi.MailingZipCode || '', state: 'neutral', errorType: 'None' },
                    mailingAddressCity: { value: pi.HomeCity || '', state: 'neutral', errorType: 'None' },
                    mailingAddressLineOne: { value: pi.HomeAddressLine1 || '', state: 'neutral', errorType: 'None' },
                    mailingAddressLineTwo: { value: pi.HomeAddressLine2 || '', state: 'neutral', errorType: 'None' },
                    mailingAddressState: { value: pi.HomeState || '', state: 'neutral', errorType: 'None' },
                    mailingAddressZipCode: { value: pi.HomeZipCode || '', state: 'neutral', errorType: 'None' },
                });
            }
            setIsHomeAddressShown(hasHomeAddress);
            return true;
        } else if (existingAppData?.Applicant && updatedPersonalInfoVersion === 'preApproved') {
            const applicant = existingAppData.Applicant;
            const hasHomeAddress = !!applicant.HomeAddress?.AddressLineOne && !applicant.IsMailingAddressSameAsHomeAddress;
            if (hasHomeAddress) {
                setHideRow(false);
            }
            const dateParts = (applicant.DateOfBirth as string).split(/[\/-]/)
            personalInformationWrite({
                firstName: { value: applicant.ApplicantName?.FirstName || '', state: 'neutral', errorType: 'None' },
                middleInitial: { value: applicant.ApplicantName?.MiddleInitial || '', state: 'neutral', errorType: 'None' },
                lastName: { value: applicant.ApplicantName?.LastName || '', state: 'neutral', errorType: 'None' },
                suffix: { value: applicant.ApplicantName?.NameSuffix || '', state: 'neutral', errorType: 'None' },
                emailAddress: { value: applicant.EmailAddress?.Value || '', state: 'neutral', errorType: 'None' },
                mailingAddressLineOne: { value: applicant.MailingAddress?.AddressLineOne || '', state: 'neutral', errorType: 'None' },
                mailingAddressLineTwo: { value: applicant.MailingAddress?.AddressLineTwo || '', state: 'neutral', errorType: 'None' },
                mailingAddressCity: { value: applicant.MailingAddress?.City || '', state: 'neutral', errorType: 'None' },
                mailingAddressState: { value: applicant.MailingAddress?.State || '', state: 'neutral', errorType: 'None' },
                mailingAddressZipCode: { value: applicant.MailingAddress?.ZipCode || '', state: 'neutral', errorType: 'None' },
                homeAddressLineOne: { value: applicant.HomeAddress?.AddressLineOne || '', state: 'neutral', errorType: 'None' },
                homeAddressLineTwo: { value: applicant.HomeAddress?.AddressLineTwo || '', state: 'neutral', errorType: 'None' },
                homeAddressCity: { value: applicant.HomeAddress?.City || '', state: 'neutral', errorType: 'None' },
                homeAddressState: { value: applicant.HomeAddress?.State || '', state: 'neutral', errorType: 'None' },
                homeAddressZipCode: { value: applicant.HomeAddress?.ZipCode || '', state: 'neutral', errorType: 'None' },
                phoneNumber: { value: applicant.PrimaryPhoneNumber?.Number || '', state: 'neutral', errorType: 'None' },
                ssn: { value: applicant.SocialSecurityNumber?.FullSocialSecurityNumber || '', state: 'neutral', errorType: 'None' },
                monthlyIncome: { value: applicant.MonthlyIncome || '', state: 'neutral', errorType: 'None' },
                dateOfBirthDay: { value: dateParts[1], state: 'neutral', errorType: 'None' },
                dateOfBirthMonth: { value: dateParts[0], state: 'neutral', errorType: 'None' },
                dateOfBirthYear: { value: dateParts[2], state: 'neutral', errorType: 'None' },
            });
            setIsHomeAddressShown(hasHomeAddress);
            return true;
        }
        return false;
    };

    const prefill = async () => {
        if (await restoreFromSavedAppData()) {
            return;
        }

        const requiredFields: (keyof TAcquisitionState)[] = [
            'firstName',
            'lastName',
            'ssn',
            'monthlyIncome',
            'dateOfBirthDay',
            'dateOfBirthMonth',
            'dateOfBirthYear',
        ]

        if (props.requirePhoneNumberEnabled) {
            requiredFields.push('phoneNumber')
        }

        if (props.requireEmailEnabled) {
            requiredFields.push('emailAddress');
        }

        const acquisitionData = getPersonalInformation();
        const fieldsWithErrors = requiredFields.reduce((acc, curr) => {
            if (!acquisitionData?.[curr]?.value) {
                acc[curr] = { errorType: 'requiredField' } as Partial<TInputField> & Partial<TBooleanField>;
            }
            return acc;
        }, {} as TAcquisitionState);

        if (Object.keys(fieldsWithErrors).length > 0) {
            personalInformationWrite(fieldsWithErrors);
        }

        if (updatedPersonalInfoVersion === 'skipPQ') {
            let postData;
            const postDataEl = document.getElementById('skip-pre-post-data');
            if (postDataEl) {
                postData = postDataEl.dataset;
            }
            if (postData && postData !== undefined) {
                const isValidFirstName = String(postData.fname ?? postData.firstName ?? '').length <= 30 && String(postData.fname ?? postData.firstName ?? '').length > 0;
                const isValidLastName = String(postData.lname ?? postData.lastName ?? '').length <= 30 && String(postData.lname ?? postData.lastName ?? '').length > 0;

                postData.firstName = isValidFirstName ? postData.fname : '';
                postData.lastName = isValidLastName ? postData.lname : '';
                postData.middleInitial = String(postData.mname ?? postData.middleInitial ?? '').toUpperCase();

                const fNameInvalidRegex = !(regexContainer.allow.first_name.test(postData.firstName));
                const lNameInvalidRegex = !(regexContainer.allow.last_name.test(postData.lastName));
                let mNameInvalidRegex;

                if (postData.middleInitial !== '') {
                    mNameInvalidRegex = !(regexContainer.allow.middle_initial.test(postData.middleInitial));
                } else {
                    mNameInvalidRegex = false;
                }

                if (postData.firstName === '' || postData.lastName === '' || fNameInvalidRegex || lNameInvalidRegex || mNameInvalidRegex) {
                    postData.firstName = '';
                    postData.lastName = '';
                    postData.middleInitial = '';
                }

                personalInformationWrite({
                    firstName: { value: postData.firstName, state: 'neutral', errorType: 'None' },
                    middleInitial: { value: postData.middleInitial, state: 'neutral', errorType: 'None' },
                    lastName: { value: postData.lastName, state: 'neutral', errorType: 'None' },
                    phoneNumber: { value: postData.mobileNumber, state: 'neutral', errorType: 'None' },
                });

                if (postData.mobileNumber) {
                    setIsPhonePrefilled(true);
                }
            }
        } else if (updatedPersonalInfoVersion === 'skipPQ-Prefilled') {
            const skipPQPostData = document.getElementById('skip-pre-post-data')?.dataset ?? {};
            const isValid = (str: string) => str && str.length <= 30;

            const skipPQPostDataUpdates = Object.entries(skipPQPostData)?.reduce((acc, [k, v]) => {
                switch (k) {
                    case 'fname':
                    case 'firstName':
                        Object.assign(acc, { firstName: { value: v, state: 'neutral', errorType: 'None' } });
                        break;
                    case 'lname':
                    case 'lastName':
                        Object.assign(acc, { lastName: { value: v, state: 'neutral', errorType: 'None' } });
                        break;
                    case 'mname':
                    case 'middleInitial':
                        Object.assign(acc, { middleInitial: { value: v.toUpperCase(), state: 'neutral', errorType: 'None' } });
                        break;
                    case 'mobileNumber':
                        setIsPhonePrefilled(true); // trigger phone validation
                        Object.assign(acc, { phoneNumber: { value: v, state: 'neutral', errorType: 'None' } });
                        break;
                    case 'homeAddress':
                        if (v?.length) {
                            setHideRow(false);
                        }
                        break;
                    default: break;
                }

                return acc;
            }, {} as TAcquisitionState);

            // if any name field is in error, clear
            if (
                !regexContainer.allow.first_name.test(skipPQPostDataUpdates?.firstName?.value ?? '') ||
                !regexContainer.allow.last_name.test(skipPQPostDataUpdates?.lastName?.value ?? '') ||
                !isValid(skipPQPostDataUpdates?.firstName?.value ?? '') ||
                !isValid(skipPQPostDataUpdates?.lastName?.value ?? '') ||
                (
                    skipPQPostDataUpdates?.middleInitial?.value !== '' &&
                    skipPQPostDataUpdates?.middleInitial?.value !== undefined &&
                    !regexContainer.allow.middle_initial.test(skipPQPostDataUpdates.middleInitial.value)
                )
            ) {
                delete skipPQPostDataUpdates?.firstName
                delete skipPQPostDataUpdates?.lastName
                delete skipPQPostDataUpdates?.middleInitial
            }

            // missing required fields should trigger field in edit
            if (['firstName', 'lastName', 'phoneNumber'].some(e => !Object.keys(skipPQPostDataUpdates).includes(e))) {
                setFirstEdit(false);
            }

            // update value, state, and error for anything that is valid prefill
            if (Object.keys(skipPQPostDataUpdates).length > 0) {
                personalInformationWrite(skipPQPostDataUpdates);
            }
        } else if (updatedPersonalInfoVersion === 'preApproved') {
            if (SessionStorage.verify('pre-approval-data')) {
                const preApprovalData = await SessionStorage.getCipher('pre-approval-data', true);

                const updatedPersonalInformation = {};
                if (preApprovalData?.Applicant?.ApplicantName) {
                    Object.assign(updatedPersonalInformation, {
                        firstName: { value: preApprovalData.Applicant.ApplicantName.FirstName, state: 'neutral', errorType: 'None' },
                        middleInitial: { value: preApprovalData.Applicant.ApplicantName.MiddleInitial, state: 'neutral', errorType: 'None' },
                        lastName: { value: preApprovalData.Applicant.ApplicantName.LastName, state: 'neutral', errorType: 'None' },
                        suffix: { value: preApprovalData.Applicant.ApplicantName.NameSuffix, state: 'neutral', errorType: 'None' },
                    });
                }

                // if home address is included then unhide the row so it renders
                // GroupedAddressFields will handle the validation
                if (preApprovalData?.Applicant?.HomeAddress?.AddressLineOne?.length) {
                    setHideRow(false);
                }

                if (preApprovalData?.Applicant?.EmailAddress?.Value) {
                    Object.assign(updatedPersonalInformation, {
                        emailAddress: { value: preApprovalData.Applicant.EmailAddress.Value, state: 'neutral', errorType: 'None' }
                    });
                }

                if (preApprovalData?.Applicant?.PrimaryPhoneNumber?.Number && preApprovalData.Applicant.PrimaryPhoneNumber.Number !== '') {
                    const phone = preApprovalData.Applicant.PrimaryPhoneNumber.Number.replace(/(\d{3})(\d{3})(\d{4})/, '($1) $2-$3');
                    Object.assign(updatedPersonalInformation, {
                        phoneNumber: { value: phone, state: 'neutral', errorType: 'None' }
                    });
                }

                if (preApprovalData?.Applicant?.SocialSecurityNumber?.FullSocialSecurityNumber) {
                    Object.assign(updatedPersonalInformation, {
                        ssn: { value: preApprovalData.Applicant.SocialSecurityNumber.FullSocialSecurityNumber, state: 'neutral', errorType: 'None' }
                    });
                }

                personalInformationWrite(updatedPersonalInformation);
                setIsEmailBlurred(true);
            }
        }
    };

    // prefill validation
    useEffect(() => {
        prefill();
    }, [isApplicationInfoRecieved]);

    // trigger full form validation
    useEffect(() => {
        if (getFeatureFlags('DPM-12192-ApplicationCtaValidationsRefactor')) {
            return;
        }
        const triggerErrors = () => {
            if (updatedPersonalInfoVersion === 'pqOfOne') {
                //PQ of One doesn't allow editing; don't check for errors
                return;
            }

            const acquisitionData = getPersonalInformation();
            const requiredFields = ['firstName', 'lastName', 'mailingAddressLineOne', 'mailingAddressCity', 'mailingAddressState',
                'mailingAddressZipCode', 'ssn', 'monthlyIncome', 'dateOfBirthDay', 'dateOfBirthMonth', 'dateOfBirthYear'];
            if (getIsHomeAddressShown()) {
                requiredFields.push(
                    'homeAddressLineOne',
                    'homeAddressCity',
                    'homeAddressState',
                    'homeAddressZipCode',
                );
            }
            // Only add mailing apt # if there is a value, otherwise optional
            if (!!acquisitionData.mailingAddressLineTwo?.value) {
                requiredFields.push('mailingAddressLineTwo');
            }
            // Only add home apt # if there is a value, otherwise optional
            if (!!acquisitionData.homeAddressLineTwo?.value) {
                requiredFields.push('homeAddressLineTwo');
            }
            if (props.requirePhoneNumberEnabled) {
                requiredFields.push('phoneNumber');
            }
            if (props.requireEmailEnabled) {
                requiredFields.push('emailAddress');
            }

            const fieldsWithErrors = requiredFields.reduce((acc, curr) => {
                if (!acquisitionData?.[curr]?.value) {
                    acc[curr] = { state: 'error', errorType: 'requiredField' } as Partial<TInputField> & Partial<TBooleanField>;
                }
                return acc;
            }, {} as TAcquisitionState);

            if (Object.keys(fieldsWithErrors).length > 0) {
                personalInformationWrite(fieldsWithErrors);
            }
        };

        window.addEventListener('acquisitionErrorToggle', triggerErrors);
        return () => {
            window.removeEventListener('acquisitionErrorToggle', triggerErrors);
        };
    }, [updatedPersonalInfoVersion, props.requirePhoneNumberEnabled, props.requireEmailEnabled]);

    useEffect(() => {
        if (!getFeatureFlags('DPM-12192-ApplicationCtaValidationsRefactor')) {
            return;
        }
        const acquisitionErrorListener = (event: AcquisitionErrorToggleEvent) => {
            if (updatedPersonalInfoVersion === 'pqOfOne') {
                //PQ of One doesn't allow editing; don't check for errors
                return;
            }

            const acquisitionData = event.validatingState.getPersonalInformation();

            const requiredFields = ['firstName', 'lastName', 'mailingAddressLineOne', 'mailingAddressCity', 'mailingAddressState',
                'mailingAddressZipCode', 'ssn', 'monthlyIncome', 'dateOfBirthDay', 'dateOfBirthMonth', 'dateOfBirthYear'];
            if (event.validatingState.getIsHomeAddressShown()) {
                requiredFields.push(
                    'homeAddressLineOne',
                    'homeAddressCity',
                    'homeAddressState',
                    'homeAddressZipCode',
                );
            }
            // Only add mailing apt # if there is a value, otherwise optional
            if (!!acquisitionData.mailingAddressLineTwo?.value) {
                requiredFields.push('mailingAddressLineTwo');
            }
            // Only add home apt # if there is a value, otherwise optional
            if (!!acquisitionData.homeAddressLineTwo?.value) {
                requiredFields.push('homeAddressLineTwo');
            }
            if (props.requirePhoneNumberEnabled) {
                requiredFields.push('phoneNumber');
            }
            if (props.requireEmailEnabled) {
                requiredFields.push('emailAddress');
            }

            const fieldsWithErrors = requiredFields.reduce((acc, curr) => {
                if (!acquisitionData?.[curr]?.value) {
                    acc[curr] = { state: 'error', errorType: 'requiredField' } as Partial<TInputField> & Partial<TBooleanField>;
                }
                return acc;
            }, {} as TAcquisitionState);

            if (Object.keys(fieldsWithErrors).length > 0) {
                event.validatingState.personalInformationWrite(fieldsWithErrors);
            }
        }

        window.addEventListener(
            AcquisitionErrorToggleEvent.EventName,
            acquisitionErrorListener
        );
        return () => {
            window.removeEventListener(
                AcquisitionErrorToggleEvent.EventName,
                acquisitionErrorListener
            );
        }
    }, [
        props.version,
        props.requirePhoneNumberEnabled,
        props.requireEmailEnabled,
    ]);

    useEffect(() => {
        if (isPhonePrefilled && getPersonalInformation().phoneNumber?.value) {
            validatePhoneNumberWithBriteVerify({
                value: getPersonalInformation().phoneNumber.value,
                valid: getPersonalInformation().phoneNumber.errorType === 'None',
            });
        }
    }, [isPhonePrefilled]);

    useEffect(() => {
        if (props.homeAddressPrimary) {
            SessionStorage.set(
                'primaryHomeABInfo',
                JSON.stringify({
                    homeAddressInputText: props.homeAddressInputText,
                    mailingAddressCheckboxText: props.mailingAddressCheckboxText,
                }),
            );
        }
        if (updatedPersonalInfoVersion === 'pqOfOne' && !props.author) {
            startValidation();
        }

        if (updatedPersonalInfoVersion === 'partnerHostedVersion' && !props.author) {
            try {
                startValidationForPartnerHosted();
            } catch (e) {
                console.error('Error during validation');
                handlePartnerRedirect();
            }
        }

        SessionStorage.setObject('grossIncomeEnabled', props.showingGrossIncomeVariant);
    }, []);

    useEffect(() => {
        if (updatedPersonalInfoVersion === 'preApproved' && !firstEdit) {
            setHideAddressOnPA(false);
        }

    }, [firstEdit]);

    const selectVersion = () => {
        const acquisitionData = getPersonalInformation();
        const skipPQ = () => {
            return (
                <>
                    <Row className='PersonalInformationForm__row'>
                        <Col id='firstName-field-container' className='PersonalInformationForm__firstName' md={4}>
                            {getFeatureFlags('DPM-13102') ? (
                                <NameFieldWithProvider
                                    fieldKey='firstName'
                                    label='First Name'
                                    name='First Name'
                                    id='firstName'
                                    onBlurCallback={() => {
                                        isSamePersonAuthorizedUserSetter();
                                    }}
                                />
                            ) : (
                                <NameField
                                    alternate
                                    label='First Name'
                                    name='First Name'
                                    id='firstName'
                                    value={personalInfoForm.firstName.value}
                                    setValueCallback={(value: string) => {
                                        setPersonalInfoForm((personalInfoForm: any) => ({ ...personalInfoForm, firstName: { value: value, valid: true } }));
                                    }}
                                    error={!personalInfoForm.firstName.valid}
                                    onBlurCallback={() => {
                                        // DPM-7649 see NameField.tsx
                                        validateField(
                                            'firstName',
                                            personalInfoForm.firstName,
                                            null,
                                            regexContainer.disallow.first_name,
                                            'FirstName',
                                        );
                                        isSamePersonAuthorizedUserSetter();
                                    }}
                                    useSuccessIcon
                                />
                            )}
                        </Col>
                        <Col className='PersonalInformationForm__middleInitial' md={4}>
                            {getFeatureFlags('DPM-13102') ? (
                                <MiddleInitialFieldWithProvider
                                    fieldKey='middleInitial'
                                    name='Middle Initial'
                                    label='Middle Initial'
                                    id='middleInitial'
                                />
                            ) : (
                                <MiddleInitialField
                                    label='Middle Initial'
                                    id='middleInitial'
                                    alternate
                                    value={personalInfoForm.middleInitial.value}
                                    setValueCallback={(value: string) => {
                                        setPersonalInfoForm((personalInfoForm: any) => ({ ...personalInfoForm, middleInitial: { value: value, valid: true } }));
                                    }}
                                    useSuccessIcon
                                />
                            )}
                        </Col>
                        <Col id='lastName-field-container' className='PersonalInformationForm__lastName' md={4}>
                            {getFeatureFlags('DPM-13102') ? (
                                <NameFieldWithProvider
                                    fieldKey='lastName'
                                    label='Last Name'
                                    name='Last Name'
                                    id='lastName'
                                    onBlurCallback={() => {
                                        isSamePersonAuthorizedUserSetter();
                                    }}
                                />
                            ) : (
                                <NameField
                                    value={personalInfoForm.lastName.value}
                                    label='Last Name'
                                    name='Last Name'
                                    id='lastName'
                                    setValueCallback={(value: string) => {
                                        setPersonalInfoForm((personalInfoForm: any) => ({ ...personalInfoForm, lastName: { value: value, valid: true } }));
                                    }}
                                    alternate
                                    error={!personalInfoForm.lastName.valid}
                                    onBlurCallback={() => {
                                        // DPM-7649 see NameField.tsx
                                        validateField(
                                            'lastName',
                                            personalInfoForm.lastName,
                                            null,
                                            regexContainer.disallow.last_name,
                                            'LastName',
                                        );
                                        isSamePersonAuthorizedUserSetter();
                                    }}
                                    useSuccessIcon
                                />
                            )}
                        </Col>
                    </Row>
                    <Row className='PersonalInformationForm__row'>
                        <GroupedAddressFields
                            version={version}
                            addressType='mailing'
                            usePOBoxRegexCheck={props.aptUnitPoBox}
                            isHomeFieldsHidden={hideRow}
                            setIsHomeFieldsHidden={setHideRow}
                            wiDisclaimer={props.wiDisclaimer}
                            wrapper={{
                                wrapperElement: Col,
                                addressWrapperProps: { id: 'mailingAddress-field-container', className: 'PersonalInformationForm__mailingAddress', md: 4 },
                                aptWrapperProps: { className: 'PersonalInformationForm__mailingAptUnit', md: 4 },
                                cityWrapperProps: { id: 'mailingCity-field-container', className: 'PersonalInformationForm__mailingCity', md: 4 },
                                stateWrapperProps: { id: 'mailingState-field-container', className: 'PersonalInformationForm__mailingState', md: 4 },
                                zipWrapperProps: { id: 'mailingZipCode-field-container', className: 'PersonalInformationForm__mailingZip', md: 4 },
                            }}
                            suggestedAddressEnabled={props.suggestedAddressEnabled}
                            suggestedAddressHeadline={props.suggestedAddressHeadline}
                            suggestedAddressSubHeadline={props.suggestedAddressSubHeadline}
                            suggestedAddressAcceptCtaText={props.suggestedAddressAcceptCtaText}
                            suggestedAddressDeclineCtaText={props.suggestedAddressDeclineCtaText}
                            avcThreshold={props.avcThreshold}
                        />
                    </Row>
                    {!hideRow && (
                        <SlideDown
                            data-testid='homeaddress-slidedown'
                            className={`PersonalInformationForm__row${hideRow ? '--hidden' : '--slide'}`}
                            onTransitionEnd={() => setHomeAddressValidateOnSubmit(true)}
                            open={!hideRow}
                        >
                            <Row className='PersonalInformationForm__row'>
                                <Col md={12} className={`PersonalInformationForm__homeNote${hideRow ? '--hidden' : '--slide'}`}>
                                    <p className='PersonalInformationForm__poBoxNote'>{transformHTMLParserLinks(wrapTrademarkSuperscript(props.mailingAddressText))}</p>
                                </Col>
                                <GroupedAddressFields
                                    version={version}
                                    addressType='home'
                                    usePOBoxRegexCheck={props.aptUnitPoBox}
                                    isHomeFieldsHidden={hideRow}
                                    setIsHomeFieldsHidden={setHideRow}
                                    wiDisclaimer={props.wiDisclaimer}
                                    wrapper={{
                                        wrapperElement: Col,
                                        addressWrapperProps: { id: 'homeAddress-field-container', className: `PersonalInformationForm__homeAddress PersonalInformationForm__homeInput${hideRow ? '' : '--display'}`, md: 4 },
                                        aptWrapperProps: { className: `PersonalInformationForm__homeAptUnit PersonalInformationForm__homeInput${hideRow ? '' : '--display'}`, md: 4 },
                                        cityWrapperProps: { id: 'homeCity-field-container', className: `PersonalInformationForm__homeCity PersonalInformationForm__homeInput${hideRow ? '' : '--display'}`, md: 4 },
                                        stateWrapperProps: { id: 'homeState-field-container', className: `PersonalInformationForm__homeState ${hideRow ? '' : 'PersonalInformationForm__homeState--display'} PersonalInformationForm__homeInput${hideRow ? '' : '--display'}`, md: 4 },
                                        zipWrapperProps: { id: 'homeZipCode-field-container', className: `PersonalInformationForm__homeZip PersonalInformationForm__homeInput${hideRow ? '' : '--display'}`, md: 4 },
                                    }}
                                    suggestedAddressEnabled={props.suggestedAddressEnabled}
                                    suggestedAddressHeadline={props.suggestedAddressHeadline}
                                    suggestedAddressSubHeadline={props.suggestedAddressSubHeadline}
                                    suggestedAddressAcceptCtaText={props.suggestedAddressAcceptCtaText}
                                    suggestedAddressDeclineCtaText={props.suggestedAddressDeclineCtaText}
                                    avcThreshold={props.avcThreshold}
                                />
                            </Row>
                        </SlideDown>
                    )}
                    <Row>
                        <Col className='PersonalInformationForm__paInfoSubhead'>
                            {transformHTMLParserLinks(paFormInformationSubhead)}
                        </Col>
                    </Row>
                    <Row className='PersonalInformationForm__row'>
                        <Col
                            id='emailAddress-field-container'
                            className={
                                `PersonalInformationForm__email 
                                ${props.requireEmailEnabled ? 'PersonalInformationForm__email--required' : ''} 
                                ${emailAutofill ? 'PersonalInformationForm__emailAutoFill' : ''}`
                            }
                            md={4}>
                            {getFeatureFlags('DPM-13566') ? (
                                <EmailFieldWithProvider
                                    fieldKey='emailAddress'
                                    name='Email Address'
                                    id='emailAddress'
                                    emailMessage={props.emailMessage}
                                    emailAutofill={emailAutofill.map(val => val.value)}
                                />
                            ) :
                                (
                                    <EmailField value={personalInfoForm.email.value} label='Email Address' alternate
                                        setValueCallback={(value: string) => {
                                            setPersonalInfoForm((personalInfoForm: any) => ({ ...personalInfoForm, email: { value: value, valid: personalInfoForm.email.valid } }));
                                        }}
                                        id='emailAddress'
                                        helperText='email@domain.com'
                                        error={!personalInfoForm.email.valid}
                                        validateEmail={validateEmailWithBriteVerify}
                                        onValidationComplete={completeEmailValidation}
                                        showIcon={showIcon}
                                        useSuccessIcon
                                        emailAutofill={emailAutofill}
                                        emailMessage={props.emailMessage}
                                        emailClassName='PersonalInformationForm__inputNote'
                                    />
                                )}
                        </Col>
                        <Col id='phone-field-container' className={`PersonalInformationForm__phoneNumber PersonalInformationForm__phoneNumber${props.requirePhoneNumberEnabled ? '--required' : ''}`} md={4}>
                            <div className='PersonalInformationForm__phoneInputContainer'>
                                {/* validphonenumber and phonemessage should live inside container */}
                                {getFeatureFlags('DPM-14217') ? (
                                    <PhoneNumberFieldWithProvider
                                        fieldKey='phoneNumber'
                                        name='Phone Number'
                                        label='Main Phone Number'
                                        helperText='XXX-XXX-XXXX'
                                    />
                                ) : (
                                    <PhoneNumberField
                                        required={props.requirePhoneNumberEnabled}
                                        alternate
                                        value={personalInfoForm.phoneNumber.value}
                                        label='Main Phone Number'
                                        helperText='XXX-XXX-XXXX'
                                        setValueCallback={(value: string) => {
                                            setPersonalInfoForm((personalInfoForm: any) => ({ ...personalInfoForm, phoneNumber: { value: value, valid: true } }));
                                        }}
                                        error={!personalInfoForm.phoneNumber.valid}
                                        onBlurCallback={async () => {
                                            await validatePhoneNumberWithBriteVerify(
                                                personalInfoForm.phoneNumber,
                                            );
                                        }}
                                        useSuccessIcon
                                        disclaimerMessage={transformHTMLParserLinks(wrapTrademarkSuperscript(props.phoneMessage))}
                                        disclaimerClassName='PersonalInformationForm__inputNote__slideDown'
                                    />
                                )}
                            </div>
                        </Col>
                    </Row>
                    <Row className='PersonalInformationForm__row'>
                        <Col id='monthlyIncome-field-container' className='PersonalInformationForm__monthlyIncome' md={4}>
                            <MonthlyIncomeField
                                ref={monthlyIncomeRef}
                                monthlyIncomeLabel='Total Monthly Income'
                                incomeMessage={props.incomeMessage}
                                incomeDescription={props.incomeDescription}
                                monthlyIncome={personalInfoForm.monthlyIncome.value}
                                handleSetValue={(value) => {
                                    setPersonalInfoForm((personalInfoForm: any) => ({ ...personalInfoForm, monthlyIncome: { value: value, valid: true } }));
                                }}
                                handleIsValidCallback={(isValid: boolean, value?: string) => {
                                    if (isValid) {
                                        const val = value !== undefined ? value : personalInfoForm.monthlyIncome.value;
                                        setErrorList('monthlyIncome', false, { value: val, valid: true }, 'MonthlyIncome');
                                    }
                                }}
                                onBlurCallback={(value: string, isValid: boolean) => {
                                    setErrorList('monthlyIncome', !isValid, { value: value, valid: isValid }, 'MonthlyIncome');
                                }}
                                popUp={props.incomePopupEnabled}
                                popUpHeadline={props.incomePopupHeadline}
                                popUpSubHeadline={props.incomePopupSubHeadline}
                                popUpCtaDismiss={props.incomePopupCtaDismiss}
                                popUpCtaKeep={props.incomePopupCtaKeep}
                                isGrossIncomeVariant={props.showingGrossIncomeVariant}
                            />
                        </Col>
                        <Col id='socialSecurity-field-container' className={`PersonalInformationForm__ssn ${props.showingGrossIncomeVariant ? 'PersonalInformationForm__grossIncomeCol' : ''}`}>
                            <form autoComplete='off' onSubmit={(e => e.preventDefault())}>
                                <SsnField
                                    inputClass='PersonalInformation__input'
                                    containerClass='PersonalInformationForm__ssnInputContainer'
                                    name='Ssn'
                                    id='ssn'
                                    label='Social Security Number'
                                    helperText='XXX-XX-XXXX'
                                    onBlurCallback={() => {
                                        setPersonalInfoErrors((prev: IPersonalInfoErrors) => {
                                            return {
                                                ...prev,
                                                ['Ssn']: (personalInfoForm.ssn.valid && personalInfoForm.ssn.value.length === 9) ? 'None' : 'NotValid',
                                            };
                                        });
                                    }
                                    }
                                    value={personalInfoForm.ssn.value}
                                    setValueCallback={(value: string) => {
                                        setPersonalInfoForm((personalInfoForm: any) => ({
                                            ...personalInfoForm,
                                            ['ssn']: { value: value, valid: personalInfoForm.ssn.valid },

                                        }));
                                    }}
                                    valid={[personalInfoForm.ssn.valid,
                                    (val: boolean) => setPersonalInfoForm((personalInfoForm: any) => ({
                                        ...personalInfoForm,
                                        ['ssn']: { value: personalInfoForm.ssn.value, valid: val },

                                    })),

                                    ]}
                                    error={!personalInfoForm.ssn.valid}
                                    disclaimerMessage={props.ssnMessage}
                                    required
                                />
                            </form>
                        </Col>
                        <Col id='dob-field-container' className={`PersonalInformationForm__dob ${props.showingGrossIncomeVariant ? 'PersonalInformationForm__grossIncomeCol' : ''}`} md={4}>
                            {getFeatureFlags('DPM-14218') ?
                                <DateOfBirthFieldWithProvider
                                    label='Date of Birth'
                                    helperText='MM-DD-YYYY'
                                    isAuthorizedUser={false}
                                    errorMessage={generateFieldErrorMessage('Date of Birth', personalInfoForm.dob.value ? null : 'missingValue')}
                                    id='dob'
                                />
                                :
                                <DateOfBirthField
                                    alternate
                                    helperText='MM-DD-YYYY'
                                    inputClass='PersonalInformationForm__input--dob'
                                    ctaId='full-app-continue'
                                    label='Date of Birth'
                                    id='dob'
                                    value={personalInfoForm.dob.value}
                                    setValueCallback={(value: string) => {
                                        setPersonalInfoForm((prev) => ({
                                            ...prev,
                                            dob: { value: value, valid: prev.dob.valid },
                                        }));
                                    }}
                                    required
                                    valid={[personalInfoForm.dob.valid,
                                    (isValid: boolean) => {
                                        setPersonalInfoForm((prev) => ({
                                            ...prev,
                                            dob: { value: prev.dob.value, valid: isValid },
                                        }));
                                        setPersonalInfoErrors((prev: IPersonalInfoErrors) => {
                                            return {
                                                ...prev,
                                                DateOfBirth: isValid ? 'None' : 'NotValid',
                                            };
                                        });
                                    },
                                    ]}
                                    applicantStateOrAu={personalInfoForm.homeState.value || personalInfoForm.mailingState.value}
                                    errorText={generateFieldErrorMessage('Date of Birth', personalInfoForm.dob.value ? null : 'missingValue')}
                                    requiredMessage={generateFieldErrorMessage('Date of Birth', personalInfoForm.dob.value ? null : 'missingValue')}
                                    onBlurCallback={() => {
                                        isSamePersonAuthorizedUserSetter();
                                    }}
                                    useSuccessIcon
                                />
                            }

                        </Col>
                        <Col id='dob-field-container' className={`md:flex flex-row ${props?.showingGrossIncomeVariant ? 'PersonalInformationForm__grossIncomeCol' : ''}`} md={4}>

                        </Col>
                    </Row>
                </>
            );
        };

        const skipPqPrefilled = () => (
            <>
                {firstEdit &&
                    <>
                        <Row>
                            <Col className='PersonalInformationForm__input--locked flex flex-col justify-between' md={4}>
                                <InputBase alternate required readOnly
                                    label='Name'
                                    className='h-auto'
                                    inputClass='PersonalInformationForm__input--locked bg-white font-bold text-grey-tundora py-0 px-1'
                                    value={acquisitionData.firstName.value + ' ' + `${acquisitionData.middleInitial.value ? (acquisitionData.middleInitial.value + ' ') : ''}` + acquisitionData.lastName.value}
                                    setValue={() => { }}
                                    disabled
                                />
                            </Col>
                            <Col className='PersonalInformationForm__editBtnContainer md:hidden'>
                                <Button className='PersonalInformationForm__editBtn' variant='link' size='medium' onClick={() => (setFirstEdit(!firstEdit), setSecondEdit(!secondEdit))}>
                                    EDIT
                                </Button>
                            </Col>
                        </Row>
                        {/* DPM-13680: Make home address primary */}
                        {props.homeAddressPrimary ?
                            <>
                                <Row>
                                    <Col className='flex flex-col'>
                                        <GroupedAddressFieldsHomePrimary
                                            isReadOnly
                                            version='skipPQ-Prefilled'
                                            labelType='home'
                                            addressType='mailing'
                                            isMailingAddressHidden={!mailingCheckboxSelected}
                                            setMailingCheckboxSelected={setMailingCheckboxSelected}
                                            setFirstEdit={setFirstEdit} />
                                    </Col>
                                </Row>
                                {!hideRow &&
                                    <Row>
                                        <Col className='flex flex-col'>
                                            <GroupedAddressFieldsHomePrimary
                                                isReadOnly
                                                version='skipPQ-Prefilled'
                                                addressType='home'
                                                labelType='mailing'
                                                isMailingAddressHidden={!mailingCheckboxSelected}
                                                setFirstEdit={setFirstEdit}
                                                setMailingCheckboxSelected={setMailingCheckboxSelected}
                                            />
                                        </Col>
                                    </Row>
                                }
                            </>
                            :
                            <>
                                <Row>
                                    <Col className='flex flex-col'>
                                        <GroupedAddressFields
                                            isReadOnly
                                            version='skipPQ-Prefilled'
                                            addressType='mailing'
                                            isHomeFieldsHidden={hideRow}
                                            setIsHomeFieldsHidden={setHideRow}
                                            setFirstEdit={setFirstEdit}
                                        />
                                    </Col>
                                </Row>
                                {!hideRow &&
                                    <Row>
                                        <Col className='flex flex-col'>
                                            <GroupedAddressFields
                                                isReadOnly
                                                version='skipPQ-Prefilled'
                                                addressType='home'
                                                isHomeFieldsHidden={hideRow}
                                                setIsHomeFieldsHidden={setHideRow}
                                                setFirstEdit={setFirstEdit}
                                            />
                                        </Col>
                                    </Row>
                                }

                            </>
                        }
                        <Row>
                            <Col className={`flex flex-col PersonalInformationForm__phoneNumber${props.requirePhoneNumberEnabled ? '--required' : ''}`}>
                                <InputBase ref={phoneInputRef} required alternate readOnly
                                    label='Main Phone Number'
                                    className='h-auto'
                                    inputClass='PersonalInformationForm__input--locked bg-white font-bold text-grey-tundora py-0 px-1'
                                    value={acquisitionData.phoneNumber.value}
                                    mask='000-000-0000'
                                    setValue={() => { }}
                                    disabled
                                />
                            </Col>
                        </Row>
                        <Row className='flex min-md:hidden'>
                            <Col className='PersonalInformationForm__editBtnContainer'>
                                <Button className='PersonalInformationForm__editBtn' variant='link' size='medium' onClick={() => setFirstEdit(!firstEdit)}>
                                    EDIT
                                </Button>
                            </Col>
                        </Row>
                    </>
                }

                {!firstEdit &&
                    <>
                        <Row className='md:flex-col'>
                            <Col id='firstName-field-container' className='PersonalInformationForm__input--allowEdit PersonalInformationForm__firstName'>
                                {getFeatureFlags('DPM-13102') ? (
                                    <NameFieldWithProvider
                                        fieldKey='firstName'
                                        label='First Name'
                                        name='First Name'
                                        id='firstName'
                                        onBlurCallback={() => {
                                            isSamePersonAuthorizedUserSetter();
                                        }}
                                    />
                                ) : (
                                    <NameField
                                        alternate
                                        label='First Name'
                                        name='First Name'
                                        id='firstName'
                                        value={personalInfoForm.firstName.value}
                                        setValueCallback={(value: string) => {
                                            setPersonalInfoForm((personalInfoForm: any) => ({ ...personalInfoForm, firstName: { value: value, valid: true } }));
                                        }}
                                        error={!personalInfoForm.firstName.valid}
                                        onBlurCallback={() => {
                                            // DPM-7649 see NameField.tsx
                                            validateField(
                                                'firstName',
                                                personalInfoForm.firstName,
                                                null,
                                                regexContainer.disallow.first_name,
                                                'FirstName',
                                            );
                                            isSamePersonAuthorizedUserSetter();
                                        }}
                                        useSuccessIcon
                                    />
                                )}
                            </Col>
                            <Col className='PersonalInformationForm__input--allowEdit PersonalInformationForm__middleInitial'>
                                {getFeatureFlags('DPM-13102') ? (
                                    <MiddleInitialFieldWithProvider
                                        fieldKey='middleInitial'
                                        name='Middle Initial'
                                        label='Middle Initial'
                                        id='middleInitial'
                                    />
                                ) : (
                                    <MiddleInitialField
                                        alternate
                                        name='Middle Initial'
                                        label='Middle Initial'
                                        id='middleInitial'
                                        value={personalInfoForm.middleInitial.value}
                                        setValueCallback={(value: string) => {
                                            setPersonalInfoForm((personalInfoForm: any) => ({ ...personalInfoForm, middleInitial: { value: value, valid: true } }));
                                        }}
                                        disabled={false}
                                        useSuccessIcon
                                    />
                                )}
                            </Col>
                            <Col id='lastName-field-container' className='PersonalInformationForm__input--allowEdit PersonalInformationForm__lastName'>
                                {getFeatureFlags('DPM-13102') ? (
                                    <NameFieldWithProvider
                                        fieldKey='lastName'
                                        label='Last Name'
                                        name='Last Name'
                                        id='lastName'
                                        onBlurCallback={() => {
                                            isSamePersonAuthorizedUserSetter();
                                        }}
                                    />
                                ) : (
                                    <NameField
                                        alternate
                                        value={personalInfoForm.lastName.value}
                                        label='Last Name'
                                        name='Last Name'
                                        id='lastName'
                                        setValueCallback={(value: string) => {
                                            setPersonalInfoForm((personalInfoForm: any) => ({ ...personalInfoForm, lastName: { value: value, valid: true } }));
                                        }}
                                        error={!personalInfoForm.lastName.valid}
                                        onBlurCallback={() => {
                                            // DPM-7649 see NameField.tsx
                                            validateField(
                                                'lastName',
                                                personalInfoForm.lastName,
                                                null,
                                                regexContainer.disallow.last_name,
                                                'LastName',
                                            );
                                            isSamePersonAuthorizedUserSetter();
                                        }}
                                        useSuccessIcon
                                    />
                                )}
                            </Col>
                        </Row>
                        {/* DPM-13680: Make home address primary */}
                        {props.homeAddressPrimary ?
                            <>
                                <Row className='md:flex-col'>
                                    <GroupedAddressFieldsHomePrimary
                                        version={version}
                                        addressType='mailing'
                                        labelType='home'
                                        usePOBoxRegexCheck={props.aptUnitPoBox}
                                        isMailingAddressHidden={!mailingCheckboxSelected}
                                        setMailingCheckboxSelected={setMailingCheckboxSelected}
                                        wiDisclaimer={props.wiDisclaimer}
                                        wrapper={{
                                            wrapperElement: Col,
                                            addressWrapperProps: { id: 'mailingAddress-field-container', className: 'PersonalInformationForm__input--allowEdit PersonalInformationForm__mailingAddress', md: 4 },
                                            aptWrapperProps: { className: 'PersonalInformationForm__input--allowEdit PersonalInformationForm__mailingAptUnit', md: 4 },
                                            cityWrapperProps: { id: 'mailingCity-field-container', className: 'PersonalInformationForm__input--allowEdit PersonalInformationForm__mailingCity', md: 4 },
                                            stateWrapperProps: { id: 'mailingState-field-container', className: 'PersonalInformationForm__input--allowEdit PersonalInformationForm__mailingState', md: 4 },
                                            zipWrapperProps: { id: 'mailingZipCode-field-container', className: 'PersonalInformationForm__input--allowEdit PersonalInformationForm__mailingZip', md: 4 },
                                        }}
                                        suggestedAddressEnabled={props.suggestedAddressEnabled}
                                        suggestedAddressHeadline={props.suggestedAddressHeadline}
                                        suggestedAddressSubHeadline={props.suggestedAddressSubHeadline}
                                        suggestedAddressAcceptCtaText={props.suggestedAddressAcceptCtaText}
                                        suggestedAddressDeclineCtaText={props.suggestedAddressDeclineCtaText}
                                        avcThreshold={props.avcThreshold}
                                        disclaimerMessage={props.homeAddressInputText}
                                    />
                                    <Col id='phone-field-container' className={`PersonalInformationForm__phoneNumber PersonalInformationForm__phoneNumber${props.requirePhoneNumberEnabled ? '--required' : ''}`}>
                                        <div className='PersonalInformationForm__phoneInputContainer'>
                                            {getFeatureFlags('DPM-14217') ? (
                                                <PhoneNumberFieldWithProvider
                                                    fieldKey='phoneNumber'
                                                    name='Phone Number'
                                                    label='Main Phone Number'
                                                    helperText='XXX-XXX-XXXX'
                                                />
                                            ) : (
                                                <PhoneNumberField
                                                    required={props.requirePhoneNumberEnabled}
                                                    alternate
                                                    value={personalInfoForm.phoneNumber.value}
                                                    label='Main Phone Number'
                                                    helperText='XXX-XXX-XXXX'
                                                    setValueCallback={(value: string) => {
                                                        setPersonalInfoForm((personalInfoForm: any) => ({
                                                            ...personalInfoForm,
                                                            ['phoneNumber']: { value: value, valid: true },
                                                        }));
                                                    }}
                                                    error={!personalInfoForm.phoneNumber.valid}
                                                    onBlurCallback={async () => {
                                                        await validatePhoneNumberWithBriteVerify(
                                                            personalInfoForm.phoneNumber,
                                                        );
                                                    }}
                                                    onFocusCallback={() => {
                                                        setHidePhoneMessage(false);
                                                    }}
                                                    useSuccessIcon
                                                    disclaimerClassName='PersonalInformationForm__inputNote'
                                                    disclaimerMessage={transformHTMLParserLinks(wrapTrademarkSuperscript(props.phoneMessage))}
                                                />
                                            )}
                                        </div>
                                    </Col>
                                </Row>
                                <Row className='md:flex-col mb-4'>
                                    <Col>
                                        <InputCheckbox label={props.mailingAddressCheckboxText && transformHTMLParserLinks(props.mailingAddressCheckboxText)}
                                            value={mailingCheckboxSelected}
                                            setValue={() => {
                                                setHideRow((prev) => !prev);
                                                setMailingCheckboxSelected((prev) => !prev);
                                            }
                                            } />
                                    </Col>
                                </Row>
                                {!hideRow &&
                                    <SlideDown
                                        data-testid='homeaddress-slidedown'
                                        className={'skipPqPrefilled ' + (hideRow ? 'hidden' : '')}
                                        onTransitionEnd={() => setHomeAddressValidateOnSubmit(true)}
                                        open={!hideRow}
                                    >
                                        <Row className='md:flex-col'>
                                            <GroupedAddressFieldsHomePrimary
                                                version={version}
                                                addressType='home'
                                                labelType='mailing'
                                                isMailingAddressHidden={!mailingCheckboxSelected}
                                                setMailingCheckboxSelected={setMailingCheckboxSelected}
                                                wiDisclaimer={props.wiDisclaimer}
                                                wrapper={{
                                                    wrapperElement: Col,
                                                    addressWrapperProps: { id: 'homeAddress-field-container', className: 'min-h-0' },
                                                    aptWrapperProps: { className: 'min-h-0' },
                                                    cityWrapperProps: { id: 'homeCity-field-container', className: 'min-h-0' },
                                                    stateWrapperProps: { id: 'homeState-field-container', className: 'min-h-0' },
                                                    zipWrapperProps: { id: 'homeZipCode-field-container', className: 'min-h-0' },
                                                }}
                                                suggestedAddressEnabled={props.suggestedAddressEnabled}
                                                suggestedAddressHeadline={props.suggestedAddressHeadline}
                                                suggestedAddressSubHeadline={props.suggestedAddressSubHeadline}
                                                suggestedAddressAcceptCtaText={props.suggestedAddressAcceptCtaText}
                                                suggestedAddressDeclineCtaText={props.suggestedAddressDeclineCtaText}
                                                avcThreshold={props.avcThreshold}
                                            />
                                        </Row>
                                    </SlideDown>
                                }
                            </>
                            :
                            <>
                                <Row className='md:flex-col'>
                                    <GroupedAddressFields
                                        version={version}
                                        addressType='mailing'
                                        usePOBoxRegexCheck={props.aptUnitPoBox}
                                        isHomeFieldsHidden={hideRow}
                                        setIsHomeFieldsHidden={setHideRow}
                                        wiDisclaimer={props.wiDisclaimer}
                                        wrapper={{
                                            wrapperElement: Col,
                                            addressWrapperProps: { id: 'mailingAddress-field-container', className: 'PersonalInformationForm__input--allowEdit PersonalInformationForm__mailingAddress', md: 4 },
                                            aptWrapperProps: { className: 'PersonalInformationForm__input--allowEdit PersonalInformationForm__mailingAptUnit', md: 4 },
                                            cityWrapperProps: { id: 'mailingCity-field-container', className: 'PersonalInformationForm__input--allowEdit PersonalInformationForm__mailingCity', md: 4 },
                                            stateWrapperProps: { id: 'mailingState-field-container', className: 'PersonalInformationForm__input--allowEdit PersonalInformationForm__mailingState', md: 4 },
                                            zipWrapperProps: { id: 'mailingZipCode-field-container', className: 'PersonalInformationForm__input--allowEdit PersonalInformationForm__mailingZip', md: 4 },
                                        }}
                                        suggestedAddressEnabled={props.suggestedAddressEnabled}
                                        suggestedAddressHeadline={props.suggestedAddressHeadline}
                                        suggestedAddressSubHeadline={props.suggestedAddressSubHeadline}
                                        suggestedAddressAcceptCtaText={props.suggestedAddressAcceptCtaText}
                                        suggestedAddressDeclineCtaText={props.suggestedAddressDeclineCtaText}
                                        avcThreshold={props.avcThreshold}
                                    />
                                    <Col id='phone-field-container' className={`PersonalInformationForm__phoneNumber PersonalInformationForm__phoneNumber${props.requirePhoneNumberEnabled ? '--required' : ''}`}>
                                        <div className='PersonalInformationForm__phoneInputContainer'>
                                            {getFeatureFlags('DPM-14217') ? (
                                                <PhoneNumberFieldWithProvider
                                                    fieldKey='phoneNumber'
                                                    name='Phone Number'
                                                    label='Main Phone Number'
                                                    helperText='XXX-XXX-XXXX'
                                                />
                                            ) : (
                                                <PhoneNumberField
                                                    required={props.requirePhoneNumberEnabled}
                                                    alternate
                                                    value={personalInfoForm.phoneNumber.value}
                                                    label='Main Phone Number'
                                                    helperText='XXX-XXX-XXXX'
                                                    setValueCallback={(value: string) => {
                                                        setPersonalInfoForm((personalInfoForm: any) => ({
                                                            ...personalInfoForm,
                                                            ['phoneNumber']: { value: value, valid: true },
                                                        }));
                                                    }}
                                                    error={!personalInfoForm.phoneNumber.valid}
                                                    onBlurCallback={async () => {
                                                        await validatePhoneNumberWithBriteVerify(
                                                            personalInfoForm.phoneNumber,
                                                        );
                                                    }}
                                                    onFocusCallback={() => {
                                                        setHidePhoneMessage(false);
                                                    }}
                                                    useSuccessIcon
                                                    disclaimerClassName='PersonalInformationForm__inputNote'
                                                    disclaimerMessage={transformHTMLParserLinks(wrapTrademarkSuperscript(props.phoneMessage))}
                                                />
                                            )}
                                        </div>
                                    </Col>
                                </Row>
                                {!hideRow &&
                                    <SlideDown
                                        data-testid='homeaddress-slidedown'
                                        className={'skipPqPrefilled ' + (hideRow ? 'hidden' : '')}
                                        onTransitionEnd={() => setHomeAddressValidateOnSubmit(true)}
                                        open={!hideRow}
                                    >
                                        <Row>
                                            <Col className={`PersonalInformationForm__homeNote${hideRow ? '--hidden' : '--slide'}`}>
                                                <p className='PersonalInformationForm__poBoxNote'>{transformHTMLParserLinks(wrapTrademarkSuperscript(props.mailingAddressText))}</p>
                                            </Col>
                                        </Row>
                                        <Row className='md:flex-col'>
                                            <GroupedAddressFields
                                                version={version}
                                                addressType='home'
                                                usePOBoxRegexCheck={props.aptUnitPoBox}
                                                isHomeFieldsHidden={hideRow}
                                                setIsHomeFieldsHidden={setHideRow}
                                                wiDisclaimer={props.wiDisclaimer}
                                                wrapper={{
                                                    wrapperElement: Col,
                                                    addressWrapperProps: { id: 'homeAddress-field-container', className: 'min-h-0' },
                                                    aptWrapperProps: { className: 'min-h-0' },
                                                    cityWrapperProps: { id: 'homeCity-field-container', className: 'min-h-0' },
                                                    stateWrapperProps: { id: 'homeState-field-container', className: 'min-h-0' },
                                                    zipWrapperProps: { id: 'homeZipCode-field-container', className: 'min-h-0' },
                                                }}
                                                suggestedAddressEnabled={props.suggestedAddressEnabled}
                                                suggestedAddressHeadline={props.suggestedAddressHeadline}
                                                suggestedAddressSubHeadline={props.suggestedAddressSubHeadline}
                                                suggestedAddressAcceptCtaText={props.suggestedAddressAcceptCtaText}
                                                suggestedAddressDeclineCtaText={props.suggestedAddressDeclineCtaText}
                                                avcThreshold={props.avcThreshold}
                                            />
                                        </Row>
                                    </SlideDown>
                                }
                            </>
                        }
                    </>
                }
                <hr className='PersonalInformationForm__hr' />
                <Row>
                    <Col className='PersonalInformationForm__paInfoSubhead'>
                        {transformHTMLParserLinks(paFormInformationSubhead)}
                    </Col>
                </Row>
                <Row className='md:flex-col'>
                    <Col id='emailAddress-field-container'
                        className={`${emailAutofill ? ' PersonalInformationForm__emailAutoFill' : ''}`}
                    >
                        {getFeatureFlags('DPM-13566') ? (
                            <EmailFieldWithProvider
                                fieldKey='emailAddress'
                                name='Email Address'
                                id='emailAddress'
                                emailMessage={props.emailMessage}
                                emailAutofill={emailAutofill.map(val => val.value)}
                            />
                        ) :
                            (
                                <EmailField alternate
                                    value={personalInfoForm.email.value}
                                    label='Email Address'
                                    id='emailAddress'
                                    setValueCallback={(value: string) => {
                                        setPersonalInfoForm((personalInfoForm: any) => ({ ...personalInfoForm, email: { value: value, valid: personalInfoForm.email.valid } }));
                                    }}
                                    helperText='email@domain.com'
                                    error={!personalInfoForm.email.valid}
                                    validateEmail={validateEmailWithBriteVerify}
                                    onValidationComplete={completeEmailValidation}
                                    showIcon={showIcon}
                                    useSuccessIcon
                                    emailAutofill={emailAutofill}
                                    emailMessage={props.emailMessage}
                                    emailClassName='PersonalInformationForm__inputNote'
                                />
                            )}
                    </Col>
                    <Col className='md:hidden' />
                    <Col className='md:hidden' />
                </Row>
                <Row className='md:flex-col'>
                    <Col id='socialSecurity-field-container' className={`${props?.showingGrossIncomeVariant ? 'PersonalInformationForm__grossIncomeCol' : ''}`}>
                        <form className='PersonalInformationForm__ssnInputContainer'
                            autoComplete='off'
                            onSubmit={(e => e.preventDefault())}>
                            <SsnField required
                                inputClass='PersonalInformation__input md:w-3/4'
                                containerClass='PersonalInformationForm__ssnInputContainer'
                                name='Ssn'
                                id='ssn'
                                label='Social Security Number'
                                helperText='XXX-XX-XXXX'
                                onBlurCallback={() => setPersonalInfoErrors((prev: IPersonalInfoErrors) => (
                                    {
                                        ...prev,
                                        Ssn: (personalInfoForm.ssn.valid && personalInfoForm.ssn.value.length === 9) ? 'None' : 'NotValid',
                                    }
                                ))}
                                value={personalInfoForm.ssn.value || ''}
                                setValueCallback={(value: string) => setPersonalInfoForm((personalInfoForm: any) => (
                                    {
                                        ...personalInfoForm,
                                        ssn: { value: value, valid: personalInfoForm.ssn.valid },
                                    }))
                                }
                                valid={
                                    [
                                        personalInfoForm.ssn.valid,
                                        (val: boolean) => setPersonalInfoForm((personalInfoForm: any) => ({
                                            ...personalInfoForm,
                                            ssn: { value: personalInfoForm.ssn.value, valid: val },
                                        })),
                                    ]}
                                error={!personalInfoForm.ssn.valid}
                                disclaimerMessage={props.ssnMessage}
                            />
                        </form>
                    </Col>
                    <Col id='dob-field-container' className={`md:flex flex-row ${props?.showingGrossIncomeVariant ? 'PersonalInformationForm__grossIncomeCol' : ''}`}>
                        {getFeatureFlags('DPM-14218') ?
                            <DateOfBirthFieldWithProvider
                                label='Date of Birth'
                                helperText='MM-DD-YYYY'
                                isAuthorizedUser={false}
                                errorMessage={generateFieldErrorMessage('Date of Birth', personalInfoForm.dob.value ? null : 'missingValue')}
                                id='dob'
                            />
                            :
                            <DateOfBirthField alternate required
                                helperText='MM-DD-YYYY'
                                inputClass='PersonalInformationForm__input'
                                ctaId='full-app-continue'
                                label='Date of Birth'
                                value={personalInfoForm.dob.value}
                                id='dob'
                                setValueCallback={(value: string) => {
                                    setPersonalInfoForm((prev) => ({
                                        ...prev,
                                        dob: { value: value, valid: prev.dob.valid },
                                    }));
                                }}
                                valid={[personalInfoForm.dob.valid,
                                (isValid: boolean) => {
                                    setPersonalInfoForm((prev) => ({
                                        ...prev,
                                        dob: { value: prev.dob.value, valid: isValid },
                                    }));
                                    setPersonalInfoErrors((prev: IPersonalInfoErrors) => {
                                        return {
                                            ...prev,
                                            DateOfBirth: isValid ? 'None' : 'NotValid',
                                        };
                                    });
                                },
                                ]}
                                applicantStateOrAu={personalInfoForm.homeState.value || personalInfoForm.mailingState.value}
                                errorText={generateFieldErrorMessage('Date of Birth', personalInfoForm.dob.value ? null : 'missingValue')}
                                requiredMessage={generateFieldErrorMessage('Date of Birth', personalInfoForm.dob.value ? null : 'missingValue')}
                                invalidErrorMessage={generateFieldErrorMessage('Date of Birth')}
                                onBlurCallback={() => isSamePersonAuthorizedUserSetter()}
                                useSuccessIcon={true}
                            />
                        }
                    </Col>
                    <Col id='monthlyIncome-field-container'>
                        <MonthlyIncomeField
                            ref={monthlyIncomeRef}
                            monthlyIncomeLabel='Total Monthly Income'
                            incomeMessage={props.incomeMessage}
                            incomeDescription={props.incomeDescription}
                            monthlyIncome={personalInfoForm.monthlyIncome.value}
                            handleSetValue={(value) => {
                                setPersonalInfoForm((personalInfoForm: any) => ({ ...personalInfoForm, monthlyIncome: { value: value, valid: true } }));
                            }}
                            handleIsValidCallback={(isValid: boolean, value?: string) => {
                                if (isValid) {
                                    const val = value !== undefined ? value : personalInfoForm.monthlyIncome.value;
                                    setErrorList('monthlyIncome', false, { value: val, valid: true }, 'MonthlyIncome');
                                }
                            }}
                            onBlurCallback={(value: string, isValid: boolean) => {
                                setErrorList('monthlyIncome', !isValid, { value: value, valid: isValid }, 'MonthlyIncome');
                            }}
                            popUp={props.incomePopupEnabled}
                            popUpHeadline={props.incomePopupHeadline}
                            popUpSubHeadline={props.incomePopupSubHeadline}
                            popUpCtaDismiss={props.incomePopupCtaDismiss}
                            popUpCtaKeep={props.incomePopupCtaKeep}
                            isGrossIncomeVariant={props.showingGrossIncomeVariant}
                        />
                    </Col>
                </Row>
            </>
        );

        const preApproved = () => {
            const retrievePaPrefillModule = (isPrefilled: boolean = true) => {
                const fullName = acquisitionData.firstName.value + ' ' + `${acquisitionData.middleInitial.value ? (acquisitionData.middleInitial.value + ' ') : ''}` + acquisitionData.lastName.value + ' ' + acquisitionData.suffix.value;
                return (
                    <>
                        {isPrefilled &&
                            <Row className={`${hideAddressOnPA ? 'PersonalInformationForm__hideOnLoad' : ''}`}>
                                <Col className='PersonalInformationForm__paPrefilledColumn'>
                                    <Row>
                                        <Col>
                                            <div className={`PersonalInformationForm__firstName PersonalInformationForm__input${firstEdit ? '--hiddenAltDisplay' : '--allowEdit'}`} >
                                                <InputBase alternate required label='Name'
                                                    value={fullName}
                                                    setValue={() => { }}
                                                    disabled={true}
                                                />
                                            </div>
                                            <div>
                                                <GroupedAddressFields
                                                    isReadOnly
                                                    version='preApproved'
                                                    addressType='mailing'
                                                    isHomeFieldsHidden={hideRow}
                                                    setIsHomeFieldsHidden={setHideRow}
                                                    setFirstEdit={setFirstEdit}
                                                />
                                            </div>
                                            {!hideRow &&
                                                <div className={`${hideRow ? 'hidden' : ''}`}>
                                                    <GroupedAddressFields
                                                        isReadOnly
                                                        version='preApproved'
                                                        addressType='home'
                                                        isHomeFieldsHidden={hideRow}
                                                        setIsHomeFieldsHidden={setHideRow}
                                                        setFirstEdit={setFirstEdit}
                                                    />
                                                </div>
                                            }
                                        </Col>
                                    </Row>
                                    <div className='PersonalInformationForm__editBtnpaPrefillContainer'>
                                        <Button className={`PersonalInformationForm__editBtn${firstEdit ? '' : '--hidden'}`} variant='link' id='editName' size='medium' onClick={() => setFirstEdit(!firstEdit)}>
                                            EDIT
                                        </Button>
                                    </div>
                                </Col>
                            </Row>
                        }
                        {!isPrefilled &&
                            <>
                                <Row className={`${props.hidingPANameAndAddressOnLoad ? 'PersonalInformationForm__hideOnLoad' : ''}`}>
                                    <Col id='firstName-field-container' className={`PersonalInformationForm__firstName PersonalInformationForm__input${firstEdit ? '--hidden' : '--allowEdit'}`} md={3}>
                                        {getFeatureFlags('DPM-13102') ? (
                                            <NameFieldWithProvider
                                                fieldKey='firstName'
                                                label='First Name'
                                                name='First Name'
                                                id='firstName'
                                                disabled={firstEdit}
                                                onBlurCallback={() => {
                                                    isSamePersonAuthorizedUserSetter();
                                                }}
                                            />
                                        ) : (
                                            <NameField
                                                alternate
                                                label='First Name'
                                                name='First Name'
                                                id='firstName'
                                                value={personalInfoForm.firstName.value}
                                                setValueCallback={(value: string) => {
                                                    setPersonalInfoForm((personalInfoForm: any) => ({ ...personalInfoForm, firstName: { value: value, valid: true } }));
                                                }}
                                                disabled={firstEdit}
                                                error={!personalInfoForm.firstName.valid}
                                                onBlurCallback={() => {
                                                    // DPM-7649 see NameField.tsx
                                                    validateField(
                                                        'firstName',
                                                        personalInfoForm.firstName,
                                                        null,
                                                        regexContainer.disallow.first_name,
                                                        'FirstName',
                                                    );
                                                    isSamePersonAuthorizedUserSetter();
                                                }}
                                                useSuccessIcon
                                            />
                                        )}
                                    </Col>
                                    <Col className={`PersonalInformationForm__middleInitial PersonalInformationForm__input${firstEdit ? '--hidden' : '--allowEdit'}`} md={2}>
                                        {getFeatureFlags('DPM-13102') ? (
                                            <MiddleInitialFieldWithProvider
                                                fieldKey='middleInitial'
                                                name='Middle Initial'
                                                label='Middle Initial'
                                                id='middleInitial'
                                                disabled={firstEdit}
                                            />
                                        ) : (
                                            <MiddleInitialField
                                                name='Middle Initial'
                                                label='Middle Initial'
                                                id='middleInitial'
                                                value={personalInfoForm.middleInitial.value}
                                                setValueCallback={(value: string) => {
                                                    setPersonalInfoForm((personalInfoForm: any) => ({ ...personalInfoForm, middleInitial: { value: value, valid: true } }));
                                                }}
                                                disabled={firstEdit}
                                                alternate
                                                useSuccessIcon
                                            />
                                        )}
                                    </Col>
                                    <Col id='lastName-field-container' className={`PersonalInformationForm__lastName PersonalInformationForm__input${firstEdit ? '--hidden' : '--allowEdit'}`} md={3}>
                                        {getFeatureFlags('DPM-13102') ? (
                                            <NameFieldWithProvider
                                                fieldKey='lastName'
                                                label='Last Name'
                                                name='Last Name'
                                                id='lastName'
                                                disabled={firstEdit}
                                                onBlurCallback={() => {
                                                    isSamePersonAuthorizedUserSetter();
                                                }}
                                            />
                                        ) : (
                                            <NameField
                                                alternate
                                                value={personalInfoForm.lastName.value}
                                                label='Last Name'
                                                name='Last Name'
                                                id='lastName'
                                                setValueCallback={(value: string) => {
                                                    setPersonalInfoForm((personalInfoForm: any) => ({ ...personalInfoForm, lastName: { value: value, valid: true } }));
                                                }}
                                                disabled={firstEdit}
                                                error={!personalInfoForm.lastName.valid}
                                                onBlurCallback={() => {
                                                    // DPM-7649 see NameField.tsx
                                                    validateField(
                                                        'lastName',
                                                        personalInfoForm.lastName,
                                                        null,
                                                        regexContainer.disallow.last_name,
                                                        'LastName',
                                                    );
                                                    isSamePersonAuthorizedUserSetter();
                                                }}
                                                useSuccessIcon
                                            />
                                        )}
                                    </Col>
                                    <Col id='suffix-field-container' className={`PersonalInformationForm__suffix PersonalInformationForm__input${firstEdit ? '--hidden' : '--allowEdit'}`} md={2}>
                                        <form autoComplete='off' onSubmit={(e => e.preventDefault())}>
                                            {getFeatureFlags('DPM-13102') ? (
                                                <SuffixFieldWithProvider
                                                    fieldKey='suffix'
                                                    label='Suffix'
                                                    name='Suffix'
                                                    disabled={firstEdit}
                                                />
                                            ) : (
                                                <SuffixField
                                                    value={personalInfoForm.suffix.value}
                                                    valid={[
                                                        personalInfoForm.suffix.valid,
                                                        (value) => {
                                                            setPersonalInfoForm((personalInfoForm) => ({ ...personalInfoForm, suffix: { valid: value, value: personalInfoForm.suffix.value } }));
                                                        },
                                                    ]}
                                                    label='Suffix'
                                                    disabled={firstEdit}
                                                    setValueCallback={(value: string) => {
                                                        setPersonalInfoForm((personalInfoForm: any) => ({ ...personalInfoForm, suffix: { value: value.replace(/[^a-zA-Z2-9']/g, '').toUpperCase(), valid: true } }));
                                                    }}
                                                    error={!personalInfoForm.suffix.valid}
                                                    onBlurCallback={(value, isValid) => {
                                                        setErrorList('suffix', !isValid, { value: value, valid: isValid }, 'NameSuffix');
                                                    }}
                                                    useSuccessIcon
                                                />
                                            )}
                                        </form>
                                    </Col>
                                </Row>
                                <Row className='PersonalInformationForm__row'>
                                    <GroupedAddressFields
                                        version='preApproved'
                                        addressType='mailing'
                                        usePOBoxRegexCheck={props.aptUnitPoBox}
                                        isHomeFieldsHidden={hideRow}
                                        setIsHomeFieldsHidden={setHideRow}
                                        wiDisclaimer={props.wiDisclaimer}
                                        wrapper={{
                                            wrapperElement: Col,
                                            addressWrapperProps: { id: 'mailingAddress-field-container', className: `PersonalInformationForm__mailingAddress PersonalInformationForm__input${secondEdit ? '--hidden' : '--allowEdit'}`, md: 4 },
                                            aptWrapperProps: { className: `PersonalInformationForm__mailingAptUnit PersonalInformationForm__input${secondEdit ? '--hidden' : '--allowEdit'}`, md: 4 },
                                            cityWrapperProps: { id: 'mailingCity-field-container', className: `PersonalInformationForm__mailingCity PersonalInformationForm__input${secondEdit ? '--hidden' : '--allowEdit'}`, md: 4 },
                                            stateWrapperProps: { id: 'mailingState-field-container', className: `PersonalInformationForm__mailingState PersonalInformationForm__input${secondEdit ? '--hidden' : '--allowEdit'}`, md: 4 },
                                            zipWrapperProps: { id: 'mailingZipCode-field-container', className: `PersonalInformationForm__mailingZip PersonalInformationForm__input${secondEdit ? '--hidden' : '--allowEdit'}`, md: 4 },
                                        }}
                                        suggestedAddressEnabled={props.suggestedAddressEnabled}
                                        suggestedAddressHeadline={props.suggestedAddressHeadline}
                                        suggestedAddressSubHeadline={props.suggestedAddressSubHeadline}
                                        suggestedAddressAcceptCtaText={props.suggestedAddressAcceptCtaText}
                                        suggestedAddressDeclineCtaText={props.suggestedAddressDeclineCtaText}
                                        avcThreshold={props.avcThreshold}
                                    />
                                </Row>
                                {!hideRow &&
                                    <SlideDown
                                        data-testid='homeaddress-slidedown'
                                        className={`PersonalInformationForm__row${hideRow ? '--hidden' : '--slide'}`}
                                        onTransitionEnd={() => setHomeAddressValidateOnSubmit(true)}
                                        open={!hideRow}
                                    >
                                        <Row className='PersonalInformationForm__row'>
                                            <Col md={12} className={`PersonalInformationForm__homeNote${hideRow ? '--hidden' : '--slide'}`}>
                                                <p className='PersonalInformationForm__poBoxNote'>{transformHTMLParserLinks(wrapTrademarkSuperscript(props.mailingAddressText))}</p>
                                            </Col>
                                            <GroupedAddressFields
                                                version='preApproved'
                                                addressType='home'
                                                usePOBoxRegexCheck={props.aptUnitPoBox}
                                                isHomeFieldsHidden={hideRow}
                                                setIsHomeFieldsHidden={setHideRow}
                                                wiDisclaimer={props.wiDisclaimer}
                                                wrapper={{
                                                    wrapperElement: Col,
                                                    addressWrapperProps: { id: 'homeAddress-field-container', className: `PersonalInformationForm__homeAddress PersonalInformationForm__homeInput${hideRow ? '' : '--display'}`, md: 4 },
                                                    aptWrapperProps: { className: `PersonalInformationForm__homeAptUnit PersonalInformationForm__homeInput${hideRow ? '' : '--display'}`, md: 4 },
                                                    cityWrapperProps: { id: 'homeCity-field-container', className: `PersonalInformationForm__homeCity PersonalInformationForm__homeInput${hideRow ? '' : '--display'}`, md: 4 },
                                                    stateWrapperProps: { id: 'homeState-field-container', className: `PersonalInformationForm__homeState ${hideRow ? '' : 'PersonalInformationForm__homeState--display'} PersonalInformationForm__homeInput${hideRow ? '' : '--display'}`, md: 4 },
                                                    zipWrapperProps: { id: 'homeZipCode-field-container', className: `PersonalInformationForm__homeZip PersonalInformationForm__homeInput${hideRow ? '' : '--display'}`, md: 4 },
                                                }}
                                                suggestedAddressEnabled={props.suggestedAddressEnabled}
                                                suggestedAddressHeadline={props.suggestedAddressHeadline}
                                                suggestedAddressSubHeadline={props.suggestedAddressSubHeadline}
                                                suggestedAddressAcceptCtaText={props.suggestedAddressAcceptCtaText}
                                                suggestedAddressDeclineCtaText={props.suggestedAddressDeclineCtaText}
                                                avcThreshold={props.avcThreshold}
                                            />
                                        </Row>
                                    </SlideDown>}
                            </>}
                    </>
                );
            };

            return (<>
                {retrievePaPrefillModule(firstEdit)}
                <hr className={`PersonalInformationForm__hr ${hideAddressOnPA ? 'PersonalInformationForm__hideOnLoad' : ''}`} />
                <Row>
                    <Col className='PersonalInformationForm__paInfoSubhead'>
                        {transformHTMLParserLinks(paFormInformationSubhead)}
                    </Col>
                </Row>
                <Row className='PersonalInformationForm__row'>
                    <Col id='emailAddress-field-container' className={`PersonalInformationForm__email PersonalInformationForm__email${props.requireEmailEnabled ? '--required' : ''} 
                    ${emailAutofill ? 'PersonalInformationForm__emailAutoFill' : ''}`} md={4}>
                        {getFeatureFlags('DPM-13566') ? (
                            <EmailFieldWithProvider
                                fieldKey='emailAddress'
                                name='Email Address'
                                id='emailAddress'
                                emailMessage={props.emailMessage}
                                emailAutofill={emailAutofill.map(val => val.value)}
                            />
                        ) :
                            (
                                <EmailField value={personalInfoForm.email.value} label='Email Address' alternate
                                    setValueCallback={(value: string) => {
                                        setPersonalInfoForm((personalInfoForm: any) => ({ ...personalInfoForm, email: { value: value, valid: personalInfoForm.email.valid } }));
                                    }}
                                    id='emailAddress'
                                    helperText='email@domain.com'
                                    error={!personalInfoForm.email.valid}
                                    validateEmail={validateEmailWithBriteVerify}
                                    onValidationComplete={completeEmailValidation}
                                    showIcon={showIcon}
                                    useSuccessIcon
                                    emailAutofill={emailAutofill}
                                    emailMessage={props.emailMessage}
                                    emailClassName='PersonalInformationForm__inputNote'
                                />
                            )}
                    </Col>
                    <Col id='phone-field-container' className={`PersonalInformationForm__phoneNumber PersonalInformationForm__phoneNumber${props.requirePhoneNumberEnabled ? '--required' : ''}`} md={4}>
                        <div className='PersonalInformationForm__phoneInputContainer'>
                            {getFeatureFlags('DPM-14217') ? (
                                <PhoneNumberFieldWithProvider
                                    fieldKey='phoneNumber'
                                    name='Phone Number'
                                    label='Main Phone Number'
                                    helperText='XXX-XXX-XXXX'
                                />
                            ) : (
                                <PhoneNumberField
                                    required={props.requirePhoneNumberEnabled}
                                    alternate
                                    value={personalInfoForm.phoneNumber.value}
                                    label='Main Phone Number'
                                    helperText='XXX-XXX-XXXX'
                                    setValueCallback={(value: string) => {
                                        setPersonalInfoForm((personalInfoForm: any) => ({ ...personalInfoForm, phoneNumber: { value: value, valid: true } }));
                                    }}
                                    error={!personalInfoForm.phoneNumber.valid}
                                    onBlurCallback={async () => {
                                        await validatePhoneNumberWithBriteVerify(
                                            personalInfoForm.phoneNumber,
                                        );
                                    }}
                                    disclaimerMessage={transformHTMLParserLinks(wrapTrademarkSuperscript(props.phoneMessage))}
                                    disclaimerClassName='PersonalInformationForm__inputNote'
                                    advancedMasking={props.advancedMasking}
                                    useSuccessIcon
                                />
                            )}
                        </div>
                    </Col>
                </Row>
                <Row className='PersonalInformationForm__row'>
                    <Col id='socialSecurity-field-container' className={`PersonalInformationForm__ssn ${props.showingGrossIncomeVariant ? 'PersonalInformationForm__grossIncomeCol' : ''}`}>
                        <form autoComplete='off' className='PersonalInformationForm__ssnInputContainer' onSubmit={(e => e.preventDefault())}>
                            <SsnField
                                inputClass='PersonalInformation__input'
                                containerClass='PersonalInformationForm__ssnInputContainer'
                                name='Ssn'
                                id='ssn'
                                label='Social Security Number'
                                helperText='XXX-XX-XXXX'
                                onBlurCallback={() => {
                                    setPersonalInfoErrors((prev: IPersonalInfoErrors) => {
                                        return {
                                            ...prev,
                                            ['Ssn']: (personalInfoForm.ssn.valid && personalInfoForm.ssn.value.length === 9) ? 'None' : 'NotValid',
                                        };
                                    });
                                }
                                }
                                value={personalInfoForm.ssn.value || ''}

                                setValueCallback={(value: string) => {
                                    setPersonalInfoForm((personalInfoForm: any) => ({
                                        ...personalInfoForm,
                                        ['ssn']: { value: value, valid: personalInfoForm.ssn.valid },

                                    }));
                                }}

                                valid={[personalInfoForm.ssn.valid,
                                (val: boolean) => setPersonalInfoForm((personalInfoForm: any) => ({
                                    ...personalInfoForm,
                                    ['ssn']: { value: personalInfoForm.ssn.value, valid: val },

                                })),

                                ]}
                                error={!personalInfoForm.ssn.valid}
                                disclaimerMessage={props.ssnMessage}
                                advancedMasking={props.advancedMasking}
                                required
                            />
                        </form>
                    </Col>
                    <Col id='dob-field-container' className={`PersonalInformationForm__dob ${props.showingGrossIncomeVariant ? 'PersonalInformationForm__grossIncomeCol' : ''}`} md={4}>
                        {getFeatureFlags('DPM-14218') ?
                            <DateOfBirthFieldWithProvider
                                label='Date of Birth'
                                helperText='MM-DD-YYYY'
                                isAuthorizedUser={false}
                                errorMessage={generateFieldErrorMessage('Date of Birth', personalInfoForm.dob.value ? null : 'missingValue')}
                                id='dob'
                            />
                            :
                            <DateOfBirthField
                                alternate
                                helperText='MM-DD-YYYY'
                                inputClass='PersonalInformationForm__input'
                                ctaId='full-app-continue'
                                label='Date of Birth'
                                id='dob'
                                value={personalInfoForm.dob.value}
                                setValueCallback={(value: string) => {
                                    setPersonalInfoForm((prev) => ({
                                        ...prev,
                                        dob: { value: value, valid: prev.dob.valid },
                                    }));
                                }}
                                required
                                valid={[personalInfoForm.dob.valid,
                                (isValid: boolean) => {
                                    setPersonalInfoForm((prev) => ({
                                        ...prev,
                                        dob: { value: prev.dob.value, valid: isValid },
                                    }));
                                    setPersonalInfoErrors((prev: IPersonalInfoErrors) => {
                                        return {
                                            ...prev,
                                            DateOfBirth: isValid ? 'None' : 'NotValid',
                                        };
                                    });
                                },
                                ]}
                                applicantStateOrAu={personalInfoForm.homeState.value || personalInfoForm.mailingState.value}
                                errorText={generateFieldErrorMessage('Date of Birth', personalInfoForm.dob.value ? null : 'missingValue')}
                                requiredMessage={generateFieldErrorMessage('Date of Birth', personalInfoForm.dob.value ? null : 'missingValue')}
                                onBlurCallback={() => {
                                    isSamePersonAuthorizedUserSetter();
                                }}
                                advancedMasking ={props.advancedMasking}
                                useSuccessIcon
                            />
                        }
                    </Col>
                    <Col id='monthlyIncome-field-container' className='PersonalInformationForm__monthlyIncome' md={4}>
                        <MonthlyIncomeField
                            ref={monthlyIncomeRef}
                            monthlyIncomeLabel='Total Monthly Income'
                            incomeMessage={props.incomeMessage}
                            incomeDescription={props.incomeDescription}
                            monthlyIncome={personalInfoForm.monthlyIncome.value}
                            handleSetValue={(value) => {
                                setPersonalInfoForm((personalInfoForm: any) => ({ ...personalInfoForm, monthlyIncome: { value: value, valid: true } }));
                            }}
                            handleIsValidCallback={(isValid: boolean, value?: string) => {
                                if (isValid) {
                                    const val = value !== undefined ? value : personalInfoForm.monthlyIncome.value;
                                    setErrorList('monthlyIncome', false, { value: val, valid: true }, 'MonthlyIncome');
                                }
                            }}
                            onBlurCallback={(value: string, isValid: boolean) => {
                                setErrorList('monthlyIncome', !isValid, { value: value, valid: isValid }, 'MonthlyIncome');
                            }}
                            popUp={props.incomePopupEnabled}
                            popUpHeadline={props.incomePopupHeadline}
                            popUpSubHeadline={props.incomePopupSubHeadline}
                            popUpCtaDismiss={props.incomePopupCtaDismiss}
                            popUpCtaKeep={props.incomePopupCtaKeep}
                            isGrossIncomeVariant={props.showingGrossIncomeVariant}
                        />
                    </Col>
                </Row>
            </>);
        };

        const pqOfOne = () => {
            return (
                <>
                    <Row>
                        <Col className='PersonalInformationForm__input--hidden' md={3}>
                            {getFeatureFlags('DPM-13102') ? (
                                <NameFieldWithProvider
                                    fieldKey='firstName'
                                    label='First Name'
                                    name='First Name'
                                    id='firstName'
                                    disabled
                                />
                            ) : (
                                <NameField
                                    alternate
                                    label='First Name'
                                    name='First Name'
                                    id='firstName'
                                    value={personalInfoForm.firstName.value}
                                    setValueCallback={() => { }}
                                    disabled={true}
                                />
                            )}
                        </Col>
                        <Col className='PersonalInformationForm__input--hidden' md={2}>
                            {getFeatureFlags('DPM-13102') ? (
                                <MiddleInitialFieldWithProvider
                                    fieldKey='middleInitial'
                                    name='Middle Initial'
                                    label='Middle Initial'
                                    id='middleInitial'
                                    disabled
                                />
                            ) : (
                                <MiddleInitialField
                                    alternate
                                    label='Middle Initial'
                                    disabled={true}
                                    value={personalInfoForm.middleInitial.value}
                                    setValueCallback={() => { }}
                                />
                            )}
                        </Col>
                        <Col className='PersonalInformationForm__input--hidden' md={3}>
                            {getFeatureFlags('DPM-13102') ? (
                                <NameFieldWithProvider
                                    fieldKey='lastName'
                                    label='Last Name'
                                    name='Last Name'
                                    id='lastName'
                                    disabled
                                />
                            ) : (
                                <NameField
                                    alternate
                                    value={personalInfoForm.lastName.value}
                                    label='Last Name'
                                    name='Last Name'
                                    id='lastName'
                                    setValueCallback={() => { }}
                                    disabled={true}
                                />
                            )}
                        </Col>
                        <Col className='PersonalInformationForm__input--hidden' md={2}>
                            {getFeatureFlags('DPM-13102') ? (
                                <SuffixFieldWithProvider
                                    fieldKey='suffix'
                                    label='Suffix'
                                    name='Suffix'
                                    disabled
                                />
                            ) : (
                                <SuffixField
                                    value={personalInfoForm.suffix.value}
                                    label='Suffix'
                                    disabled={true}
                                />
                            )}
                        </Col>
                    </Row>
                    <hr className='PersonalInformationForm__hr' />
                    <Row className='PersonalInformationForm__row'>
                        <Col className='PersonalInformationForm__input--hidden' md={4}>
                            <InputBase alternate
                                label='Mailing Address'
                                value={personalInfoForm.mailingAddress.value}
                                setValue={() => { }}
                                disabled={true}
                                id='mailingAddressLine1'
                            />
                        </Col>
                        <Col className='PersonalInformationForm__input--hidden' md={2}>
                            <InputBase alternate
                                disabled={true}
                                value={personalInfoForm.mailingAptUnit.value}
                                label='Apt/Unit #'
                                setValue={() => { }}
                                id='mailingAddressLine2'
                            />
                        </Col>
                        <Col className='PersonalInformationForm__input--hidden' md={4}>
                            <InputBase alternate
                                label='City'
                                disabled={true}
                                value={personalInfoForm.mailingCity.value}
                                setValue={() => { }}
                                id='mailingAddressCity'
                            />
                        </Col>
                    </Row>
                    <Row className='PersonalInformationForm__row'>
                        <Col className='PersonalInformationForm__input--hidden PersonalInformationForm__stateInput' md={4} >
                            <p className='PersonalInformationForm__inputOverride--title'>
                                State
                            </p>
                            <p className='PersonalInformationForm__inputOverride--value'>
                                {personalInfoForm.mailingState.value}
                            </p>
                            {personalInfoForm.mailingState.value === 'WI' &&
                                <p className='PersonalInformationForm__note'><strong>WISCONSIN: </strong>{transformHTMLParserLinks(wrapTrademarkSuperscript(props.wiDisclaimer))}</p>
                            }                        </Col>
                        <Col className='PersonalInformationForm__input--hidden' md={2}>
                            <InputBase alternate
                                label='ZIP Code'
                                value={personalInfoForm.mailingZip.value}
                                setValue={() => { }}
                                id='mailingAddressZip'
                                disabled={true}
                            />
                        </Col>
                        <Col md={4} />
                    </Row>
                    <hr className='PersonalInformationForm__hr' />
                    {!hideRow &&
                        <SlideDown
                            data-testid='homeaddress-slidedown'
                            className={`PersonalInformationForm__row${hideRow ? '--hidden' : '--slide'}`}
                            onTransitionEnd={() => setHomeAddressValidateOnSubmit(true)}
                            open={!hideRow}
                        >
                            <Row className='PersonalInformationForm__row'>
                                <Col md={4}>
                                    <p className='PersonalInformationForm__inputOverride--title'>
                                        Home Address
                                    </p>
                                    <p className='PersonalInformationForm__inputOverride--value'>
                                        {personalInfoForm.homeAddress.value}
                                    </p>
                                </Col>
                                <Col md={2}>
                                    <p className='PersonalInformationForm__inputOverride--title'>
                                        Apt/Unit #
                                    </p>
                                    <p className='PersonalInformationForm__inputOverride--value'>
                                        {personalInfoForm.homeAptUnit.value}
                                    </p>
                                </Col>
                                <Col md={4}>
                                    <p className='PersonalInformationForm__inputOverride--title'>
                                        City
                                    </p>
                                    <p className='PersonalInformationForm__inputOverride--value'>
                                        {personalInfoForm.homeCity.value}
                                    </p>
                                </Col>
                            </Row>
                            <Row className='PersonalInformationForm__row'>
                                <Col className='PersonalInformationForm__input--hidden PersonalInformationForm__stateInput' md={4} >
                                    <p className='PersonalInformationForm__inputOverride--title'>
                                        State
                                    </p>
                                    <p className='PersonalInformationForm__inputOverride--value'>
                                        {personalInfoForm.homeState.value}
                                    </p>
                                    {personalInfoForm.homeState.value === 'WI' &&
                                        <p className='PersonalInformationForm__note'><strong>WISCONSIN: </strong>{transformHTMLParserLinks(wrapTrademarkSuperscript(props.wiDisclaimer))}</p>
                                    }
                                </Col>
                                <Col md={4} >
                                    <p className='PersonalInformationForm__inputOverride--title'>
                                        ZIP Code
                                    </p>
                                    <p className='PersonalInformationForm__inputOverride--value'>
                                        {personalInfoForm.homeZip.value}
                                    </p>
                                </Col>
                                <Col md={4} />
                            </Row>
                            <hr className='PersonalInformationForm__hr' />
                        </SlideDown>}
                    <Row className='PersonalInformationForm__row'>
                        <Col className='PersonalInformationForm__input-hidden' md={4}>
                            <p className='PersonalInformationForm__inputOverride--title'>
                                Social Security Number
                            </p>
                            <p className='PersonalInformationForm__inputOverride--value'>
                                ***-**-{personalInfoForm.ssn.value
                                }</p>
                        </Col>
                        <Col className='PersonalInformationForm__dob' md={4}>
                            <InputBase alternate
                                value={personalInfoForm.dob.value}
                                label='Date of Birth'
                                setValue={() => { }}
                                readOnly={true}
                            />
                        </Col>
                        <Col className={`PersonalInformationForm__email${props.requireEmailEnabled ? '--required' : ''} 
                    ${emailAutofill ? 'PersonalInformationForm__emailAutoFill' : ''}`} md={4}>
                            {getFeatureFlags('DPM-13566') ? (
                                <EmailFieldWithProvider
                                    fieldKey='emailAddress'
                                    name='Email Address'
                                    id='emailAddress'
                                    disabled={true}
                                />
                            ) :
                                (
                                    <EmailField value={personalInfoForm.email.value}
                                        label='Email Address'
                                        id='emailAddress'
                                        alternate
                                        setValueCallback={() => { }}
                                        readOnly={true}
                                    />
                                )}
                            {personalInfoForm.email.value && <p className='PersonalInformationForm__inputNote'>{transformHTMLParserLinks(wrapTrademarkSuperscript(props.emailMessage))}</p>}
                        </Col>
                    </Row>
                </>
            );
        };

        const partnerHostedVersion = () => (
            <>
                <Row>
                    <Col className='PersonalInformationForm__input--hidden' md={3}>
                        {getFeatureFlags('DPM-13102') ? (
                            <NameFieldWithProvider
                                fieldKey='firstName'
                                label='First Name'
                                name='First Name'
                                id='firstName'
                                disabled
                            />
                        ) : (
                            <NameField
                                alternate
                                label='First Name'
                                name='First Name'
                                id='firstName'
                                value={personalInfoForm.firstName.value}
                                setValueCallback={() => { }}
                                disabled={true}
                            />
                        )}
                    </Col>
                    <Col className='PersonalInformationForm__input--hidden' md={2}>
                        <InputBase alternate
                            label='Middle Initial'
                            disabled={true}
                            value={personalInfoForm.middleInitial.value}
                            setValue={() => { }}
                        />
                    </Col>
                    <Col className='PersonalInformationForm__input--hidden' md={3}>
                        {getFeatureFlags('DPM-13102') ? (
                            <NameFieldWithProvider
                                fieldKey='lastName'
                                label='Last Name'
                                name='Last Name'
                                id='lastName'
                                disabled
                            />
                        ) : (
                            <NameField
                                alternate
                                value={personalInfoForm.lastName.value}
                                label='Last Name'
                                name='Last Name'
                                id='lastName'
                                setValueCallback={() => { }}
                                disabled={true}
                            />
                        )}
                    </Col>
                    <Col className='PersonalInformationForm__input--hidden' md={2}>
                        <InputBase alternate
                            value={personalInfoForm.suffix.value}
                            label='Suffix'
                            disabled={true}
                            setValue={() => { }}
                            maxLength={4}
                        />
                    </Col>
                </Row>
                <hr className='PersonalInformationForm__hr' />
                <Row className='PersonalInformationForm__row'>
                    <Col className='PersonalInformationForm__input--hidden' md={4}>
                        <InputBase alternate
                            label='Mailing Address'
                            value={personalInfoForm.mailingAddress.value}
                            setValue={() => { }}
                            disabled={true}
                            id='mailingAddressLine1'
                        />
                    </Col>
                    <Col className='PersonalInformationForm__input--hidden' md={2}>
                        <InputBase alternate
                            disabled={true}
                            value={personalInfoForm.mailingAptUnit.value} label='Apt/Unit #'
                            setValue={() => { }}
                            id='mailingAddressLine2'
                        />
                    </Col>
                    <Col className='PersonalInformationForm__input--hidden' md={4}>
                        <InputBase alternate
                            label='City'
                            disabled={true}
                            value={personalInfoForm.mailingCity.value}
                            setValue={() => { }}
                            id='mailingAddressCity'
                        />
                    </Col>
                </Row>
                <Row className='PersonalInformationForm__row'>
                    <Col className='PersonalInformationForm__input--hidden PersonalInformationForm__stateInput' md={4} >
                        <p className='PersonalInformationForm__inputOverride--title'>
                            State
                        </p>
                        <p className='PersonalInformationForm__inputOverride--value'>
                            {personalInfoForm.mailingState.value}
                        </p>
                        {personalInfoForm.mailingState.value === 'WI' &&
                            <p className='PersonalInformationForm__note'><strong>WISCONSIN: </strong>{transformHTMLParserLinks(wrapTrademarkSuperscript(props.wiDisclaimer))}</p>
                        }                        </Col>
                    <Col className='PersonalInformationForm__input--hidden' md={2}>
                        <InputBase alternate
                            label='ZIP Code'
                            value={personalInfoForm.mailingZip.value}
                            setValue={() => { }}
                            id='mailingAddressZip'
                            disabled={true}
                        />
                    </Col>
                    <Col md={4} />
                </Row>
                <hr className='PersonalInformationForm__hr' />
                {!hideRow &&
                    <SlideDown
                        data-testid='homeaddress-slidedown'
                        className={`PersonalInformationForm__row${hideRow ? '--hidden' : '--slide'}`}
                        onTransitionEnd={() => setHomeAddressValidateOnSubmit(true)}
                        open={!hideRow}
                    >
                        <Row className='PersonalInformationForm__row'>
                            <Col md={4}>
                                <p className='PersonalInformationForm__inputOverride--title'>
                                    Home Address
                                </p>
                                <p className='PersonalInformationForm__inputOverride--value'>
                                    {personalInfoForm.homeAddress.value}
                                </p>
                            </Col>
                            <Col md={2}>
                                <p className='PersonalInformationForm__inputOverride--title'>
                                    Apt/Unit #
                                </p>
                                <p className='PersonalInformationForm__inputOverride--value'>
                                    {personalInfoForm.homeAptUnit.value}
                                </p>
                            </Col>
                            <Col md={4}>
                                <p className='PersonalInformationForm__inputOverride--title'>
                                    City
                                </p>
                                <p className='PersonalInformationForm__inputOverride--value'>
                                    {personalInfoForm.homeCity.value}
                                </p>
                            </Col>
                        </Row>
                        <Row className='PersonalInformationForm__row'>
                            <Col className='PersonalInformationForm__input--hidden PersonalInformationForm__stateInput' md={4} >
                                <p className='PersonalInformationForm__inputOverride--title'>
                                    State
                                </p>
                                <p className='PersonalInformationForm__inputOverride--value'>
                                    {personalInfoForm.homeState.value}
                                </p>
                                {personalInfoForm.homeState.value === 'WI' &&
                                    <p className='PersonalInformationForm__note'><strong>WISCONSIN: </strong>{transformHTMLParserLinks(wrapTrademarkSuperscript(props.wiDisclaimer))}</p>
                                }
                            </Col>
                            <Col md={4} >
                                <p className='PersonalInformationForm__inputOverride--title'>
                                    ZIP Code
                                </p>
                                <p className='PersonalInformationForm__inputOverride--value'>
                                    {personalInfoForm.homeZip.value}
                                </p>
                            </Col>
                            <Col md={4} />
                        </Row>
                        <hr className='PersonalInformationForm__hr' />
                    </SlideDown>}
                <Row className='PersonalInformationForm__row'>
                    <Col className='PersonalInformationForm__input-hidden' md={4}>
                        <p className='PersonalInformationForm__inputOverride--title'>
                            Main Phone Number
                        </p>
                        <p className='PersonalInformationForm__inputOverride--value'>
                            {acquisitionData.phoneNumber.value}</p>
                        <p className='PersonalInformationForm__note'>{transformHTMLParserLinks(wrapTrademarkSuperscript(props.phoneMessage))}</p>
                    </Col>

                    <Col className='PersonalInformationForm__input-hidden' md={4}>
                        <p className='PersonalInformationForm__inputOverride--title'>
                            Date of Birth
                        </p>
                        <p className='PersonalInformationForm__inputOverride--value'>
                            {personalInfoForm.dob.value.replaceAll('/', '-')}</p>
                    </Col>

                    <Col className='PersonalInformationForm__input-hidden' md={4}>
                        <p className='PersonalInformationForm__inputOverride--title'>
                            Email Address
                        </p>
                        <p className='PersonalInformationForm__inputOverride--value'>
                            {personalInfoForm.email.value}</p>
                        {personalInfoForm.email.value && <p className='PersonalInformationForm__inputNote'>{transformHTMLParserLinks(wrapTrademarkSuperscript(props.emailMessage))}</p>}
                    </Col>
                </Row>
                <Row className='PersonalInformationForm__row'>
                    <Col className='PersonalInformationForm__input-hidden' md={4}>
                        <p className='PersonalInformationForm__inputOverride--title'>
                            Total Monthly Income
                        </p>
                        <p className='PersonalInformationForm__inputOverride--value'>
                            {personalInfoForm.monthlyIncome.value}</p>
                        {personalInfoForm.monthlyIncome.value && <p className='PersonalInformationForm__inputNote'>{transformHTMLParserLinks(wrapTrademarkSuperscript(props.incomeMessage))}</p>}
                    </Col>
                </Row>
            </>
        );

        if (!props.isInEditor && !showOnAppEnabled) {
            return;
        }
        switch (updatedPersonalInfoVersion) {
            case 'skipPQ':
                return skipPQ();
            case 'skipPQ-Prefilled':
                return skipPqPrefilled();
            case 'preApproved':
                return preApproved();
            case 'partnerHostedVersion':
                return partnerHostedVersion();
            default:
                return pqOfOne();
        }
    };

    return (
        <>
            {!hidePIOnPQOfOne && <div id='personal-information-form' className={`PersonalInformationForm PersonalInformationForm${props.topPaddingEnabled ? '--topPaddingEnabled' : ''} PersonalInformationForm--${updatedPersonalInfoVersion}`}>
                {props.topBorderEnabled && <div className='PersonalInformationForm__borderContainer'>
                    <div className='PersonalInformationForm__border' />
                </div>}
                <Section className='PersonalInformationForm__section'
                    containerClass='PersonalInformationForm__container'>
                    <h4 className='PersonalInformationForm__headline'>
                        {transformHTMLParserLinks(wrapTrademarkSuperscript(props.headline))}
                    </h4>
                    <form name='form'>
                        <div className='PersonalInformationForm__fieldContainer'>
                            <h4 className={`PersonalInformationForm__title ${hideAddressOnPA ? 'PersonalInformationForm__hideOnLoad' : ''}`}>{transformHTMLParserLinks(wrapTrademarkSuperscript(props.title))}</h4>
                            <div className='PersonalInformationForm__note'>{transformHTMLParserLinks(subtitle)}</div>
                            {selectVersion()}
                        </div>
                    </form>
                </Section>
                {props.bottomBorderEnabled && <div className='PersonalInformationForm__borderContainer'>
                    <div className='PersonalInformationForm__border' />
                </div>}
            </div>}
        </>
    );
};

export default PersonalInformationForm;
